import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Row, Col, Modal, Button, Collapse } from "react-bootstrap";
import Select from "react-select";
import { Line, Pie } from "react-chartjs-2";
import { ReactComponent as FiStar } from "../../assets/images/star-icon.svg";
import usePlan from "../../hooks/usePlan";
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiHeart,
  FiMapPin,
  FiPlus,
  FiShare2,
  FiX,
} from "react-icons/fi";
import { ReactComponent as CrimeIcon } from "../../assets/images/shild.svg";
import { ReactComponent as HospitalsIcon } from "../../assets/images/hospitals-icon.svg";
import { ReactComponent as HomePriceIcon } from "../../assets/images/home-trend.svg";
import { ReactComponent as CalculatorIcon } from "../../assets/images/mortage-calculator.svg";
import { ReactComponent as MoneyIcon } from "../../assets/images/money.svg";
import { ReactComponent as ShareIcon } from "../../assets/images/share-detail.svg";
import { ReactComponent as CompareIcon } from "../../assets/images/compare.svg";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getSearchDetailTourSteps from "./searchDetailTourSteps";
import { useSelector, useDispatch } from "react-redux";
import {
  getCity,
  getSchools,
  searchBookmark,
  getBookmarks,
} from "../../redux/slices/searchSlice";
import { getAllStates, getAllCities } from "../../redux/slices/authSlice";
import { getHospitalData } from "../../redux/slices/comparison.Slice";
import { ReactComponent as HospitalIcon } from "../../assets/images/hospital-orange.svg";
import { ReactComponent as ChevronRightIcon } from "../../assets/images/btn-right-aero.svg";
import "./searchdetails.scss";
import "./cityDetailNew.scss";
import { getJobsbyStateAndCityData } from "../../redux/slices/jobsSlice";
import Jobcards from "./jobcard";
import usePageTracking from "../../hooks/usePageTracking";
import { useTheme } from "../../context/ThemeContext";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { Copy, Edit2 } from "lucide-react";
import RestrictionModal from "../restriction-modals/RestrictionModal";
import jobViewService from "../../api/jobViewService";
import { toast } from "sonner";

const StarIcon = () => <FiStar />;

/* ── Info tooltip — small (i) icon. Self-contained (inline styles) so it
   doesn't depend on this page's stylesheets. Desktop (real mouse) hovers
   in/out; touch devices tap-to-toggle + tap-outside-to-close (hover alone
   flickers open/closed on tap — mobile browsers synthesize a
   mouseenter+mouseleave pair before the click lands). Position is computed
   from the icon's actual screen rect (fixed, viewport-clamped) so it can't
   get cut off near a narrow column's edge. ─────────────────────────────── */
const isHoverCapable = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const INFO_TIP_WIDTH = 220;
const INFO_TIP_MARGIN = 12;

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const hoverCapable = useRef(isHoverCapable()).current;

  const reposition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(INFO_TIP_WIDTH, vw - INFO_TIP_MARGIN * 2);
    const iconCenter = rect.left + rect.width / 2;
    const left = Math.max(
      INFO_TIP_MARGIN,
      Math.min(iconCenter - width / 2, vw - width - INFO_TIP_MARGIN),
    );
    const arrowLeft = Math.max(10, Math.min(iconCenter - left, width - 10));
    setPos({ left, bottom: window.innerHeight - rect.top + 8, width, arrowLeft });
  };

  useLayoutEffect(() => {
    if (!show) return undefined;
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [show]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
        flexShrink: 0,
        // Several accordion headers on this page (e.g. the desktop
        // Demographics grid) disable pointer events and hide any <svg> on
        // their button via a page-wide CSS rule once the header becomes a
        // static (non-collapsible) heading at wider viewports. Both must be
        // re-enabled inline since inline styles win over non-!important
        // stylesheet rules.
        pointerEvents: "auto",
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={hoverCapable ? () => setShow(false) : undefined}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          width: 14,
          height: 14,
          color: "var(--mmp-text-secondary)",
          cursor: "pointer",
          display: "inline-block",
          pointerEvents: "auto",
        }}
        aria-label="More info"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShow((s) => !s);
        }}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-5" />
        <path d="M12 8h.01" />
      </svg>
      {show && pos && (
        <div
          role="tooltip"
          style={{
            position: "fixed",
            left: pos.left,
            bottom: pos.bottom,
            background: "#0f172a",
            color: "#fff",
            fontSize: 12,
            fontWeight: 400,
            textTransform: "none",
            letterSpacing: "normal",
            lineHeight: 1.4,
            padding: "8px 10px",
            borderRadius: 8,
            width: pos.width,
            zIndex: 2000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
          }}
        >
          {text}
          <div
            style={{
              position: "absolute",
              bottom: -4,
              left: pos.arrowLeft,
              transform: "translateX(-50%) rotate(45deg)",
              width: 8,
              height: 8,
              background: "#0f172a",
            }}
          />
        </div>
      )}
    </span>
  );
};

const SearchDetailHeader = () => {
  // Guided tour — auto-starts on first visit, replayable from the header.
  // Licensing renders as separate cards on desktop but one section on mobile,
  // so track the breakpoint reactively and rebuild the steps when it changes.
  const [isDesktopView, setIsDesktopView] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  useEffect(() => {
    const onResize = () => setIsDesktopView(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const searchDetailTourSteps = useMemo(
    () => getSearchDetailTourSteps(isDesktopView),
    [isDesktopView],
  );
  const { isOpen: isTourOpen, finish: finishTour } = useTour("search-detail");

  const location = useLocation();
  const [activePlan, setActivePlan] = useState("For Sale");
  const [selectedPlan, setSelectedPlan] = useState("For Sale");
  const [distance, setDistance] = useState(20);

  const profileStore = useSelector((state) => state.profile);
  // Defaults align with the CSV-derived "Monthly Mortgage Payment (7.065%
  // APR)" column the API exposes (see app/webservices/admin/state.controller.js
  // — that column is ingested as-is from an upstream spreadsheet at 7.065%
  // APR with 0% down). Matching those assumptions here means our PMT calc
  // approximates the API's starting EST. MORTGAGE; changes to home price /
  // down payment / rate / term then scale predictably from there.
  const [downPaymentPct, setDownPaymentPct] = useState(20);
  const [loanTerm, setLoanTerm] = useState(30);
  const [interestRate, setInterestRate] = useState(6.85);
  const [affordEdited, setAffordEdited] = useState(false);
  const [homePrice, setHomePrice] = useState(0);
  const DEFAULT_UTILITIES = 500;
  const [affordUtilities, setAffordUtilities] = useState(DEFAULT_UTILITIES);
  const [showUtilitiesUpgradeModal, setShowUtilitiesUpgradeModal] = useState(
    false,
  );
  const [showUtilitiesEditModal, setShowUtilitiesEditModal] = useState(false);
  const [utilitiesDraft, setUtilitiesDraft] = useState(
    String(DEFAULT_UTILITIES),
  );

  const openUtilitiesEditor = () => {
    if (!isProUser) {
      setShowUtilitiesUpgradeModal(true);
      return;
    }
    setUtilitiesDraft(String(affordUtilities));
    setShowUtilitiesEditModal(true);
  };

  const saveUtilitiesDraft = () => {
    const parsed = Number(utilitiesDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setAffordUtilities(DEFAULT_UTILITIES);
    } else {
      setAffordUtilities(parsed);
    }
    setShowUtilitiesEditModal(false);
  };
  const [shareModal, setShareModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savingBookmark, setSavingBookmark] = useState(false);
  const [showSaveCityModal, setShowSaveCityModal] = useState(false);

  const openShareModal = (e) => {
    e?.stopPropagation?.();
    setShareModal(true);
  };
  const closeShareModal = () => setShareModal(false);

  const shareCityToSocialMedia = (platform) => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const cd = selectedCityData?.city_details;
    const cityName = cd?.city || "this city";
    const stateName = cd?.state || "";
    const location = stateName ? `${cityName}, ${stateName}` : cityName;

    const shareTitle = `Discover ${location} on MapMyPay`;
    const shareBody = `Get detailed information about jobs, salaries, cost of living, housing, education, healthcare, and lifestyle in ${cityName} to find the right place for your next opportunity.`;
    const shareText = `${shareTitle}\n${shareBody}\n\n🔗 ${url}`;
    const twitterText = shareText;
    const longText = shareText;
    const linkedInText = shareText;

    let target = "";
    switch (platform) {
      case "twitter":
        target = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          twitterText,
        )}`;
        break;
      case "facebook":
        target = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url,
        )}&quote=${encodeURIComponent(twitterText)}`;
        break;
      case "whatsapp":
        target = `https://wa.me/?text=${encodeURIComponent(longText)}`;
        break;
      case "linkedin":
        target = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url,
        )}&summary=${encodeURIComponent(linkedInText)}`;
        break;
      case "copy":
        navigator.clipboard
          .writeText(longText)
          .then(() => toast.success("Copied to clipboard!"))
          .catch(() => toast.error("Failed to copy"));
        closeShareModal();
        return;
      default:
        return;
    }
    if (target) {
      window.open(
        target,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      closeShareModal();
    }
  };

  const distanceOptions = [
    { value: 5, label: "5 mi" },
    { value: 10, label: "10 mi" },
    { value: 15, label: "15 mi" },
    { value: 20, label: "20 mi" },
    { value: 30, label: "30 mi" },
    { value: 50, label: "50 mi" },
  ];

  const schoolsResultsStore = useSelector(
    (state) => state.search.schools || [],
  );
  const selectedCityData = useSelector(
    (state) => state.search.selectedCityData,
  );
  const dispatchHook = useDispatch();

  // Check whether this city is already bookmarked when the city loads.
  useEffect(() => {
    const cd = selectedCityData?.city_details;
    if (!cd?.city || !cd?.state) return;

    const checkBookmark = async () => {
      try {
        const result = await dispatchHook(
          getBookmarks({ page: 1, limit: 100 }),
        );
        const bookmarks = result?.payload?.data?.data || [];
        const role = cd.role || selectedCityData?.role;
        const found = bookmarks.some(
          (b) =>
            b.city === cd.city &&
            b.state === cd.state &&
            (role ? b.role === role : true),
        );
        setIsSaved(found);
      } catch (err) {
        console.error("Failed to check bookmark status:", err);
      }
    };
    checkBookmark();
  }, [
    selectedCityData?.city_details?.city,
    selectedCityData?.city_details?.state,
  ]);

  const toggleSaveCity = async (e) => {
    e?.stopPropagation?.();
    const cd = selectedCityData?.city_details;
    if (!cd?.city || !cd?.state || savingBookmark) return;
    setSavingBookmark(true);
    try {
      await dispatchHook(
        searchBookmark({
          city: cd.city,
          state: cd.state,
          role: cd.role || selectedCityData?.role,
        }),
      ).unwrap();
      setIsSaved((prev) => !prev);
    } catch (err) {
      if (err?.status === 403) {
        setShowSaveCityModal(true);
      } else {
        console.error("Bookmark toggle failed:", err);
      }
    } finally {
      setSavingBookmark(false);
    }
  };

  const getRedfinZipUrl = (zip) => {
    if (!zip) return "https://www.redfin.com";
    return `https://www.redfin.com/zipcode/${zip}`;
  };

  const city = selectedCityData?.city_details?.city;
  const state = selectedCityData?.city_details?.state;

  const [address, setAddress] = useState();
  useEffect(() => {
    if (city && state) {
      const lowerCity = city.toLowerCase();
      const lowerState = state.toLowerCase();

      setAddress(`${lowerCity}, ${lowerState}`);
    }
  }, [city, state]);

  const getZillowLink = async (address, radiusMiles, isRent = false) => {
    const query = address;
    const geoUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
      query,
    )}&format=json&limit=1`;

    try {
      const response = await fetch(geoUrl);
      const data = await response.json();

      if (data.length === 0) throw new Error("Location not found");

      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);

      const latOffset = radiusMiles / 69;
      const lonOffset = radiusMiles / (69 * Math.cos((lat * Math.PI) / 180));

      if (isRent) {
        const rentalSearchState = {
          pagination: {},
          mapBounds: {
            north: lat + latOffset,
            south: lat - latOffset,
            east: lon + lonOffset,
            west: lon - lonOffset,
          },
          isMapVisible: true,
          isListVisible: true,
          mapZoom: 11,
          regionSelection: [],
          filterState: {
            fr: { value: true },
            fsba: { value: false },
            fsbo: { value: false },
            nc: { value: false },
            cmsn: { value: false },
            auc: { value: false },
            fore: { value: false },
            sort: { value: "globalrelevance" },
          },
          isMapSearch: true,
        };

        const encodedState = encodeURIComponent(
          JSON.stringify(rentalSearchState),
        );
        const finalUrl = `https://www.zillow.com/homes/for_rent/?searchQueryState=${encodedState}`;
        console.log("Zillow Rentals Link:", finalUrl);
        return finalUrl;
      } else {
        const saleSearchState = {
          pagination: {},
          mapBounds: {
            north: lat + latOffset,
            south: lat - latOffset,
            east: lon + lonOffset,
            west: lon - lonOffset,
          },
          isMapVisible: true,
          isListVisible: true,
          mapZoom: 9,
          filterState: {
            isAllHomes: { value: true },
            sortSelection: { value: "globalrelevance" },
          },
        };

        const encodedState = encodeURIComponent(
          JSON.stringify(saleSearchState),
        );
        const finalUrl = `https://www.zillow.com/homes/for_sale/?searchQueryState=${encodedState}`;
        console.log("Zillow For Sale Link:", finalUrl);
        return finalUrl;
      }
    } catch (error) {
      console.error("Error generating Zillow link:", error);
      return null;
    }
  };

  const searchOnZillow = async () => {
    if (!address) return;

    const isRent = activePlan === "For Rent";

    const finalUrl = await getZillowLink(address, distance, isRent);

    if (finalUrl) {
      window.open(finalUrl, "_blank");
    }
  };

  const propertyData = {
    "For Sale": {
      cards: [
        {
          label: "Typical Home Value for the Region",
          value: selectedCityData?.city_details?.home_prices
            ? `$${Number(
                selectedCityData.city_details.home_prices,
              ).toLocaleString("en-US")}`
            : "--",

          icon: <HomePriceIcon />,
        },
        {
          label: "Estimated mortgage",
          value: selectedCityData?.city_details?.annual_mortgage_payments
            ? `$${Number(
                (
                  selectedCityData.city_details.annual_mortgage_payments / 12
                ).toFixed(0),
              ).toLocaleString("en-US")}`
            : "--",
          suffix: "/month",
          icon: <CalculatorIcon />,
        },
        {
          label: "Leftover income",
          value: selectedCityData?.city_details?.left_over_annually_if_owning
            ? `$${Number(
                (
                  selectedCityData.city_details.left_over_annually_if_owning /
                  12
                ).toFixed(0),
              ).toLocaleString("en-US")}`
            : "--",

          suffix: "/month",
          icon: <MoneyIcon />,
        },
      ],
      buttons: {
        redfin: {
          label: "View homes for sale on Redfin",
          link: getRedfinZipUrl(selectedCityData?.city_details?.zip),
        },
        zillow: {
          label: "View homes for sale on Zillow",
        },
      },
    },

    "For Rent": {
      cards: [
        {
          label: "Median rent",
          value: selectedCityData?.city_details?.average_monthly_rent_prices
            ? `$${Number(
                selectedCityData.city_details.average_monthly_rent_prices,
              ).toLocaleString("en-US", {
                maximumFractionDigits: 0,
              })}`
            : "--",

          suffix: "/month",
          icon: <HomePriceIcon />,
        },
        {
          label: "Estimated utilities",
          value: selectedCityData?.city_details?.annual_taxes_paid
            ? `$${Number(
                (selectedCityData.city_details.annual_taxes_paid / 12).toFixed(
                  0,
                ),
              ).toLocaleString("en-US")}`
            : "--",
          suffix: "/month",
          icon: <CalculatorIcon />,
        },
        {
          label: "Leftover income",
          value: selectedCityData?.city_details?.left_over_annually_if_renting
            ? `$${Number(
                (
                  selectedCityData.city_details.left_over_annually_if_renting /
                  12
                ).toFixed(0),
              ).toLocaleString("en-US")}`
            : "--",
          suffix: "/month",
          icon: <MoneyIcon />,
        },
      ],
      buttons: {
        redfin: {
          label: "View rentals on Redfin",
          link:
            getRedfinZipUrl(selectedCityData?.city_details?.zip) + "/rentals",
        },
        zillow: {
          label: "View rentals on Zillow",
        },
      },
    },
  };
  const currentData = propertyData[activePlan];

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [jobs, setjobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [isJobsExpanded, setIsJobsExpanded] = useState(true);
  const [isHospitalsExpanded, setIsHospitalsExpanded] = useState(false);
  const [isLicenseReqExpanded, setIsLicenseReqExpanded] = useState(true);
  const [liveCityInput, setLiveCityInput] = useState("");
  const [liveLocOpen, setLiveLocOpen] = useState(false);
  const [liveLocPos, setLiveLocPos] = useState(null);
  const [liveSelectedState, setLiveSelectedState] = useState(null);
  const [liveSelectedCity, setLiveSelectedCity] = useState(null);
  const [liveStateSearch, setLiveStateSearch] = useState("");
  const [liveCitySearch, setLiveCitySearch] = useState("");
  const [liveCitiesLoading, setLiveCitiesLoading] = useState(false);
  const liveLocTriggerRef = useRef(null);
  const liveLocPopoverRef = useRef(null);
  const [isCeusExpanded, setIsCeusExpanded] = useState(true);
  const [isSpecialReqExpanded, setIsSpecialReqExpanded] = useState(true);
  const [isCompactStateExpanded, setIsCompactStateExpanded] = useState(true);
  const [
    isImportantConsiderationsExpanded,
    setIsImportantConsiderationsExpanded,
  ] = useState(true);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [jobViewLimitInfo, setJobViewLimitInfo] = useState(null);
  const jobViewLimitDataRef = useRef(null);
  const [selectedLevel, setSelectedLevel] = useState({
    value: "Elementary",
    label: "Elementary",
  });
  const hasTrackedViewRef = React.useRef(false);
  const { from, hospitalData, pathname, searchData, hospitalMapData } =
    location.state || {};
  const store = useSelector((state) => state.comparison);

  const hospitalList = store?.hospitalItems?.data?.hospitals
    ? store?.hospitalItems?.data?.hospitals
    : [];

  const pieArray = useSelector(
    (state) => state.search.selectedCityData?.pie_data,
  );
  const { schoolsLoading } = useSelector((state) => state.search);
  const SchoolDetails = useSelector(
    (state) => state.search.selectedCityData?.city_details,
  );
  const authUser = useSelector((state) => state.auth?.user);
  const authStoreForLive = useSelector((state) => state.auth);
  const liveAllStates = authStoreForLive?.stateItems?.states || [];
  const liveAllCities = authStoreForLive?.cityItems?.cities || [];
  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser } = usePlan();
  useEffect(() => {
    if (id) {
      dispatch(getCity({ id }));
    }
  }, [dispatch, id]);

  // Always fetch hospitals for the city being viewed on this page — not
  // just when the user arrived here via a hospital-specific link.
  // Note: selectedCityData?.city_details?.role is a display string (e.g.
  // "Nurse Midwives"), not the numeric describe_role_id the hospital-info
  // API expects — using it would silently return zero hospitals. Fall back
  // to the viewer's own nursing role id instead, same as Dashboard's
  // HospitalMap widget (the only other working caller of this endpoint).
  const hospitalInfoProfile = profileStore?.profileItem?.data;
  const hospitalInfoRole =
    hospitalInfoProfile?.nursing_role?.id ||
    hospitalInfoProfile?.role ||
    authUser?.nursing_role?.id ||
    authUser?.role ||
    null;
  useEffect(() => {
    if (!city || !state || !hospitalInfoRole) return;
    dispatch(getHospitalData({ state, city, role: hospitalInfoRole }));
  }, [dispatch, city, state, hospitalInfoRole]);

  // Pre-fetch job-view usage so clicking a job card shows the restriction
  // modal immediately instead of navigating first.
  useEffect(() => {
    let cancelled = false;
    jobViewService
      .usage()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        if (data?.limit_reached) {
          const limitData = {
            planSlug: data.plan_slug || "free",
            used: data.used ?? 5,
            limit: data.limit ?? 5,
          };
          jobViewLimitDataRef.current = limitData;
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Preload states for the Live Here, Work There dropdown.
  useEffect(() => {
    if (!Array.isArray(liveAllStates) || liveAllStates.length === 0) {
      dispatch(getAllStates());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cities for the picked state inside the live dropdown.
  useEffect(() => {
    if (!liveSelectedState) return;
    setLiveCitiesLoading(true);
    Promise.resolve(
      dispatch(getAllCities({ search_state: liveSelectedState.state })),
    ).finally(() => setLiveCitiesLoading(false));
  }, [liveSelectedState, dispatch]);

  // Reset the in-popover search box when switching between state/city views.
  useEffect(() => {
    if (liveSelectedState) {
      setLiveCitySearch("");
    } else {
      setLiveStateSearch("");
    }
  }, [liveSelectedState]);

  // Position the popover next to the trigger, flipping above when below
  // doesn't fit, and cap max-height to the available viewport space so the
  // dropdown never gets clipped against the screen edge.
  useEffect(() => {
    if (!liveLocOpen) return undefined;
    const update = () => {
      if (!liveLocTriggerRef.current) return;
      const rect = liveLocTriggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const PAD = 8;
      const GAP = 6;
      const POPOVER_DEFAULT_MAX_H = 320;

      const width = Math.min(rect.width, vw - 2 * PAD);
      let left = rect.left;
      if (left + width > vw - PAD) left = vw - PAD - width;
      if (left < PAD) left = PAD;

      const spaceBelow = vh - rect.bottom - GAP - PAD;
      const spaceAbove = rect.top - GAP - PAD;
      const wantBelow = Math.min(POPOVER_DEFAULT_MAX_H, spaceBelow);
      const wantAbove = Math.min(POPOVER_DEFAULT_MAX_H, spaceAbove);

      let top;
      let maxHeight;
      // Prefer below; flip up only when the space above is materially
      // larger (the popover would otherwise barely fit below).
      if (spaceBelow >= POPOVER_DEFAULT_MAX_H || spaceBelow >= spaceAbove) {
        top = rect.bottom + GAP;
        maxHeight = Math.max(120, wantBelow);
      } else {
        maxHeight = Math.max(120, wantAbove);
        top = rect.top - GAP - maxHeight;
      }

      setLiveLocPos({ top, left, width, maxHeight });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [liveLocOpen]);

  // Outside-click + Escape to close the popover.
  useEffect(() => {
    if (!liveLocOpen) return undefined;
    const onDown = (e) => {
      if (
        liveLocTriggerRef.current &&
        liveLocTriggerRef.current.contains(e.target)
      )
        return;
      if (
        liveLocPopoverRef.current &&
        liveLocPopoverRef.current.contains(e.target)
      )
        return;
      setLiveLocOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setLiveLocOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [liveLocOpen]);

  // Seed home-price state from the API value when a city loads. Resetting
  // `affordEdited` here so navigating to a new city restarts the calculator
  // in "synced with API" mode (no PMT override yet).
  useEffect(() => {
    const apiHomePrice = Number(
      selectedCityData?.city_details?.home_prices || 0,
    );
    setHomePrice(apiHomePrice);
    setAffordEdited(false);
  }, [selectedCityData?.city_details?.home_prices, selectedCityData?.id]);
  useEffect(() => {
    const city = selectedCityData?.city_details?.city;
    const state = selectedCityData?.city_details?.state;

    if (!city || !state) return;
    if (hasTrackedViewRef.current) return;

    hasTrackedViewRef.current = true;

    const cityName = `${city}, ${state}`;

    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: cityName,
        content_type: "city",
      });
    }
  }, [selectedCityData]);

  useEffect(() => {
    const getJobs = async () => {
      if (!city || !state) return;

      setJobsLoading(true);
      try {
        const result = await dispatch(
          getJobsbyStateAndCityData({ city, state }),
        );
        setjobs(result.payload?.data.data || []);
        console.log(result, "jobs result");
      } catch (error) {
        console.error("Failed to fetch jobs:", error);
      } finally {
        setJobsLoading(false);
      }
    };

    getJobs();
  }, [dispatch, city, state]);

  useEffect(() => {
    if (!SchoolDetails?.state || !SchoolDetails?.city) return;

    const payload = {
      state: SchoolDetails.state,
      city: SchoolDetails.city,
      level_codes: selectedLevel.value,
    };

    dispatch(getSchools(payload));
  }, [dispatch, selectedLevel, SchoolDetails?.state, SchoolDetails?.city]);

  const handleSchoolClick = (school) => {
    const website = (school?.website || "").trim();

    const hasWebsite = website.length > 0;
    const validWebsite =
      hasWebsite && !website.startsWith("http")
        ? `https://${website}`
        : hasWebsite
        ? website
        : null;

    const allWebsites =
      schoolsResultsStore?.length > 0
        ? schoolsResultsStore.map((s) => s.website)
        : null;

    if (validWebsite) {
      window.open(validWebsite, "_blank", "noopener,noreferrer");
    } else {
      setTimeout(() => setShowModal(true), 0);
    }
  };

  const chartPieData = React.useMemo(() => {
    if (!pieArray || !Array.isArray(pieArray) || pieArray.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = pieArray.map((p) => p.name ?? p.label ?? p.text ?? "");

    const data = pieArray.map((p) => {
      if (typeof p.value === "number") return p.value;
      if (typeof p.val === "number") return p.val;
      if (typeof p.amount === "number") return p.amount;
      if (typeof p.text === "string")
        return Number(p.text.replace(/[^0-9.-]+/g, "")) || 0;
      return 0;
    });

    const defaultColors = resolvedTheme === "dark"
      ? [
          "#5B9BC7", // Income Tax — brand-700
          "#3FAEE8", // Mortgage — brand-500
          "#2A4A5E", // Left Over — brand-200
          "#BB63F7",
          "#FFB701",
        ]
      : [
          "#015F88", // Income Tax — brand-700
          "#0395D3", // Mortgage — brand-500
          "#A8DBF0", // Left Over — brand-200
          "#BB63F7",
          "#FFB701",
        ];
    // Always use the design palette so the donut matches the legend dots
    // (the API data carries its own ad-hoc colors — ignore them).
    const backgroundColor = pieArray.map(
      (p, i) => defaultColors[i % defaultColors.length],
    );

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 0,
        },
      ],
    };
  }, [pieArray, resolvedTheme]);

  const temperatureData = selectedCityData?.temperature_data;
  const monthLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const highTemps = monthLabels.map((m) => {
    const raw =
      temperatureData?.[`High_${m}`] ?? temperatureData?.[`High ${m}`] ?? null;
    if (raw === null || raw === undefined) return null;
    const n = Number(String(raw).replace(/[^0-9.-]+/g, ""));
    return isNaN(n) ? null : n;
  });

  const lowTemps = monthLabels.map((m) => {
    const raw =
      temperatureData?.[`Low_${m}`] ?? temperatureData?.[`Low ${m}`] ?? null;
    if (raw === null || raw === undefined) return null;
    const n = Number(String(raw).replace(/[^0-9.-]+/g, ""));
    return isNaN(n) ? null : n;
  });

  const highTempColor = resolvedTheme === "dark" ? "#fb923c" : "#f97316";
  const lowTempColor = resolvedTheme === "dark" ? "#3FAEE8" : "#0395d3";
  const lineData = {
    labels: monthLabels.map((m) => m.toUpperCase()),
    datasets: [
      {
        label: "High",
        data: highTemps,
        borderColor: highTempColor,
        backgroundColor: "rgba(249, 115, 22, 0.18)",
        pointBackgroundColor: highTempColor,
        pointBorderColor: highTempColor,
        tension: 0.4,
        fill: 1,
      },
      {
        label: "Low",
        data: lowTemps,
        borderColor: lowTempColor,
        backgroundColor: "rgba(148, 163, 184, 0.22)",
        pointBackgroundColor: lowTempColor,
        pointBorderColor: lowTempColor,
        tension: 0.4,
        fill: "origin",
      },
    ],
  };

  const isLargeScreen =
    typeof window !== "undefined" && window.innerWidth >= 1200;
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false, // let the container control the height to avoid CSS scaling blur
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "line",
          boxWidth: 24,
          boxHeight: 2,
          padding: 16,
          font: { size: 12, weight: "600" },
          color: resolvedTheme === "dark" ? "#edf3f5" : "#364152",
        },
      },
      title: { display: false },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 3, hitRadius: 8, hoverRadius: 5 },
    },
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          font: { size: 11, weight: "700" },
          color: resolvedTheme === "dark" ? "#9aa7ac" : "#6b7280",
          // On narrow viewports (≤767px) shorten each month label to its
          // first letter so all 12 fit without overlap. Desktop keeps the
          // full three-letter label ("JAN", "FEB", …).
          callback(value) {
            const raw = this.getLabelForValue
              ? this.getLabelForValue(value)
              : String(value);
            if (typeof window !== "undefined" && window.innerWidth < 768) {
              return raw.charAt(0);
            }
            return raw;
          },
        },
        grid: { display: false },
      },
      y: {
        ticks: {
          callback: (v) => `${v}°`,
          padding: isLargeScreen ? 12 : 6,
          maxTicksLimit: isLargeScreen ? 6 : 5,
          font: { size: 11 },
          color: resolvedTheme === "dark" ? "#9aa7ac" : "#6b7280",
        },
        grid: { display: false },
      },
    },
  };
  const formatPercent = (val) => {
    if (!val) return 0;
    const num = parseFloat(String(val).replace("%", ""));
    return isNaN(num) ? 0 : num;
  };

  // The hospital arrived-via-link (if any) is always the one shown first;
  // otherwise just the first hospital returned for this city.
  const selectedHospital = hospitalData || hospitalMapData;
  const primaryHospital = selectedHospital || hospitalList?.[0];
  // Everything else for this city — revealed only when the card is expanded.
  const otherHospitals = hospitalList.filter(
    (h) => h.id !== primaryHospital?.id,
  );
  const hospitalCountLabel =
    hospitalList.length === 0
      ? "Hospitals"
      : hospitalList.length === 1
      ? "1 Hospital"
      : `${hospitalList.length} Hospitals`;

  // Clicking a hospital opens its careers page in a new tab. No-op if the
  // hospital has no careers_url on file.
  const handleHospitalCareersClick = (careersUrl) => {
    if (!careersUrl) return;
    window.open(careersUrl, "_blank", "noopener,noreferrer");
  };
  function getblueShades(percent) {
    percent = Math.max(0, Math.min(percent, 100));

    // Apply 5% stepping for clear visible color shifts
    const stepped = Math.round(percent / 5) * 5;

    // Lightest shade (0%) — softened version of #8CD7F7
    const start = { r: 210, g: 245, b: 255 };

    // Darkest shade (100%) — #8CD7F7
    const end = { r: 140, g: 215, b: 247 };

    const r = Math.round(start.r + (end.r - start.r) * (stepped / 100));
    const g = Math.round(start.g + (end.g - start.g) * (stepped / 100));
    const b = Math.round(start.b + (end.b - start.b) * (stepped / 100));

    return `rgb(${r}, ${g}, ${b})`;
  }

  function getPurpleShade(percent) {
    percent = Math.max(0, Math.min(percent, 100));

    // Apply 5% stepping (stronger visible color changes)
    const stepped = Math.round(percent / 5) * 5;

    // Lightest shade (0%)
    const start = { r: 245, g: 220, b: 255 };

    // Darkest shade #8600FF (100%)
    const end = { r: 134, g: 0, b: 255 };

    const r = Math.round(start.r + (end.r - start.r) * (stepped / 100));
    const g = Math.round(start.g + (end.g - start.g) * (stepped / 100));
    const b = Math.round(start.b + (end.b - start.b) * (stepped / 100));

    return `rgb(${r}, ${g}, ${b})`;
  }

  function getRedShade(percent) {
    percent = Math.max(0, Math.min(percent, 100));

    // Apply 5% stepping for stronger visible color jumps
    const stepped = Math.round(percent / 5) * 5;

    // Lightest shade (0%)
    const start = { r: 255, g: 230, b: 230 };

    // Darkest shade (100%) — #FF0000
    const end = { r: 255, g: 0, b: 0 };

    const r = Math.round(start.r + (end.r - start.r) * (stepped / 100));
    const g = Math.round(start.g + (end.g - start.g) * (stepped / 100));
    const b = Math.round(start.b + (end.b - start.b) * (stepped / 100));

    return `rgb(${r}, ${g}, ${b})`;
  }

  function getOrangeShade(percent) {
    percent = Math.max(0, Math.min(percent, 100));

    // Convert to nearest 5% step → makes color visibly change every 5%
    const stepped = Math.round(percent / 5) * 5;

    const start = { r: 255, g: 220, b: 180 };
    const end = { r: 255, g: 104, b: 0 };

    const r = Math.round(start.r + (end.r - start.r) * (stepped / 100));
    const g = Math.round(start.g + (end.g - start.g) * (stepped / 100));
    const b = Math.round(start.b + (end.b - start.b) * (stepped / 100));

    return `rgb(${r}, ${g}, ${b})`;
  }
  // function getCrimeShades(percent) {
  //   percent = Math.max(0, Math.min(percent, 100));

  //   // Apply 5% stepping
  //   const stepped = Math.round(percent / 5) * 5;

  //   // Lightest shade (0%) — very soft teal
  //   const start = { r: 220, g: 250, b: 245 };

  //   // Darkest shade (100%) — strong teal (#14B8A6 type)
  //   const end = { r: 20, g: 184, b: 166 };

  //   const r = Math.round(start.r + (end.r - start.r) * (stepped / 100));
  //   const g = Math.round(start.g + (end.g - start.g) * (stepped / 100));
  //   const b = Math.round(start.b + (end.b - start.b) * (stepped / 100));

  //   return `rgb(${r}, ${g}, ${b})`;
  // }
  function getCrimeShades(percent) {
    percent = Math.max(0, Math.min(percent, 100));

    const stepped = Math.round(percent / 5) * 5;

    // FILL COLOR (progress)
    const start = { r: 220, g: 248, b: 245 }; // very light
    const end = { r: 16, g: 185, b: 165 }; // strong teal

    const r = Math.round(start.r + (end.r - start.r) * (stepped / 100));
    const g = Math.round(start.g + (end.g - start.g) * (stepped / 100));
    const b = Math.round(start.b + (end.b - start.b) * (stepped / 100));

    return `rgb(${r}, ${g}, ${b})`;
  }
  function getCrimeBgShade() {
    return "rgb(160, 210, 205)";
  }
  usePageTracking("CITY_DETAILS");
  useEffect(() => {
    const payload = {
      page_name: "CITY_DETAILS",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "CITY_DETAILS",
        }),
      );
    };
  }, [dispatch]);

  function getGradePercentage(grade) {
    if (!grade) return null; // keep N/A behavior

    const map = {
      "A+": 100,
      A: 95,
      "A-": 90,
      "B+": 85,
      B: 80,
      "B-": 75,
      "C+": 70,
      C: 65,
      "C-": 60,
      "D+": 55,
      D: 50,
      "D-": 45,
      F: 30,
    };

    return map[grade] ?? 10; // 👈 fallback for unknown values
  }
  // function getGradePercentage(grade) {
  //   if (!grade) return null;

  //   const map = {
  //     "A+": 100,
  //     A: 95,
  //     "A-": 90,
  //     "B+": 85,
  //     B: 80,
  //     "B-": 75,
  //     "C+": 70,
  //     C: 65,
  //     "C-": 60,
  //     "D+": 55,
  //     D: 50,
  //     "D-": 45,
  //     F: 30,
  //   };

  //   return map[grade] ?? null; // if unknown → null
  // }
  const grade = selectedCityData?.city_details?.overall_crime_grade;

  const percent = getGradePercentage(grade);
  const violentGrade = selectedCityData?.city_details?.violent_crime_grade;
  const violentPercent = getGradePercentage(violentGrade);
  const propertyGrade = selectedCityData?.city_details?.property_crime_grade;
  const propertyPercent = getGradePercentage(propertyGrade);
  const otherGrade = selectedCityData?.city_details?.other_crime_grade;
  const otherPercent = getGradePercentage(otherGrade);

  // ── Affordability calculator ──────────────────────────────────────────
  // Defaults read from API. As soon as the user touches the down-payment
  // slider, loan-term tabs, or interest-rate field, switch to a live PMT
  // calculation so the mortgage / leftover / % take-home update in place.
  // Home price flows from local state (editable in the calculator). The state
  // is seeded from city_details.home_prices when the city loads.
  const affordHomePrice = Number(homePrice) || 0;
  const affordMonthlyRent = Number(
    selectedCityData?.city_details?.average_monthly_rent_prices || 0,
  );
  const affordApiAnnualMortgage = Number(
    selectedCityData?.city_details?.annual_mortgage_payments || 0,
  );
  const affordApiAnnualLeftover = Number(
    selectedCityData?.city_details?.left_over_annually_if_owning || 0,
  );
  const affordNetAnnualSalary = Number(
    selectedCityData?.city_details?.net_annual_salary || 0,
  );
  const affordMonthlyTakeHome =
    affordNetAnnualSalary > 0
      ? affordNetAnnualSalary / 12
      : (affordApiAnnualMortgage + affordApiAnnualLeftover) / 12;
  const affordUtilitiesNum = Number(affordUtilities) || 0;

  const affordPrincipal = affordHomePrice * (1 - downPaymentPct / 100);
  const affordMonthlyRate = interestRate / 100 / 12;
  const affordNumPayments = loanTerm * 12;
  const affordComputedMonthly =
    affordMonthlyRate === 0
      ? affordPrincipal / affordNumPayments
      : (affordPrincipal *
          affordMonthlyRate *
          Math.pow(1 + affordMonthlyRate, affordNumPayments)) /
        (Math.pow(1 + affordMonthlyRate, affordNumPayments) - 1);

  // Always use our PMT calc so the mortgage scales predictably with every
  // input. The API's mortgage value is from an opaque CSV (no source formula
  // exposed) — using it as the "starting" value caused a jump the moment any
  // input was touched. Aligning to PMT(7.065%, 0% down, 30yr) at default
  // inputs approximates the API value while keeping all downstream math
  // (LEFT OVER, donut chart, calculator) internally consistent.
  const affordCalcLive = affordHomePrice > 0;
  const affordMonthlyMortgage = affordCalcLive
    ? affordComputedMonthly
    : affordApiAnnualMortgage / 12;
  const affordLeftoverMonthly =
    affordMonthlyTakeHome - affordMonthlyMortgage - affordUtilitiesNum;
  const affordPctOfTakeHome =
    affordMonthlyTakeHome > 0
      ? (affordMonthlyMortgage / affordMonthlyTakeHome) * 100
      : 0;
  const affordPctClamped = Math.max(0, Math.min(100, affordPctOfTakeHome));
  const affordTier =
    affordPctOfTakeHome >= 35
      ? "unaffordable"
      : affordPctOfTakeHome >= 28
      ? "stretched"
      : "affordable";
  const affordLabel =
    affordTier === "unaffordable"
      ? "Likely unaffordable"
      : affordTier === "stretched"
      ? "Stretching the budget"
      : "Likely affordable";
  const affordOwnVsRentDiff = affordMonthlyMortgage - affordMonthlyRent;

  // For-Rent tab: leftover = take-home - rent - utilities. Utilities is the
  // same editable (Pro) value used by the buy tab; default $500.
  const affordRentLeftover =
    affordMonthlyTakeHome - affordMonthlyRent - affordUtilitiesNum;
  const affordRentAllIn = affordMonthlyRent + affordUtilitiesNum;
  const affordRole = selectedCityData?.city_details?.role || "";

  const markAffordEdited = () => {
    if (!affordEdited) setAffordEdited(true);
  };

  // ── Live recompute for the top "LEFT OVER EACH MONTH" card and the
  // "Where your money goes each month" donut chart when the user edits
  // MEDIAN HOME PRICE (or any other calculator input).
  //
  // The donut's percentages are computed against monthly_gross_salary,
  // which the API expresses as tax + mortgage + leftover. We hold
  // tax constant and shift dollars between mortgage and leftover so the
  // proportions stay consistent with the API model.
  const monthlyTaxPaid = Number(
    selectedCityData?.city_details?.monthly_tax_paid || 0,
  );
  const baseMonthlyMortgage = Number(
    selectedCityData?.city_details?.monthly_mortgage_payment_7_065_apr || 0,
  );
  const baseMonthlyLeftOver = Number(
    selectedCityData?.city_details?.left_over || 0,
  );
  const monthlyGrossSalary =
    monthlyTaxPaid + baseMonthlyMortgage + baseMonthlyLeftOver;

  const liveOwningLeftoverMonthly = affordCalcLive
    ? affordMonthlyTakeHome - affordMonthlyMortgage
    : Number(
        selectedCityData?.city_details?.left_over_annually_if_owning || 0,
      ) / 12;

  const liveDonutMortgageValue = affordCalcLive
    ? affordMonthlyMortgage
    : baseMonthlyMortgage;
  const liveDonutLeftOverValue = affordCalcLive
    ? monthlyGrossSalary - monthlyTaxPaid - affordMonthlyMortgage
    : baseMonthlyLeftOver;

  const liveDonutMortgagePct =
    monthlyGrossSalary > 0
      ? (liveDonutMortgageValue / monthlyGrossSalary) * 100
      : 0;
  const liveDonutLeftOverPct =
    monthlyGrossSalary > 0
      ? (liveDonutLeftOverValue / monthlyGrossSalary) * 100
      : 0;

  const liveChartPieData = React.useMemo(() => {
    if (
      !affordCalcLive ||
      !chartPieData?.datasets?.length ||
      !Array.isArray(chartPieData.datasets[0]?.data)
    ) {
      return chartPieData;
    }
    // The original pie_data array can carry values in mixed units (percent
    // strings, dollar amounts, etc). When we override, all slices MUST use
    // the same unit (dollars) so Chart.js renders correct proportions —
    // otherwise a $11,724 mortgage slice next to a "34" tax slice makes tax
    // look like a thin sliver.
    const data = [...chartPieData.datasets[0].data];
    // Pie/donut can't render negative slices; clamp to 0 visually while the
    // legend still shows the real (possibly negative) dollar value.
    if (data.length >= 1) data[0] = Math.max(0, monthlyTaxPaid);
    if (data.length >= 2) data[1] = Math.max(0, liveDonutMortgageValue);
    if (data.length >= 3) data[2] = Math.max(0, liveDonutLeftOverValue);
    return {
      ...chartPieData,
      datasets: [{ ...chartPieData.datasets[0], data }],
    };
  }, [
    chartPieData,
    affordCalcLive,
    monthlyTaxPaid,
    liveDonutMortgageValue,
    liveDonutLeftOverValue,
  ]);

  return (
    <>
      {selectedCityData ? (
        <div className="search-details-content">
          <div className="header-top-row">
            <FiArrowLeft
              size={20}
              className="back-icon"
              onClick={() => {
                if (from === "search") navigate("/search");
                else if (from === "hospital" && pathname === "/full-map") {
                  navigate("/full-map");
                } else if (from === "pieChart" || from === "dashboard") {
                  navigate("/dashboard");
                } else navigate(-1);
              }}
            />
            <h1 className="city-state-title">
              {selectedCityData?.city_details?.city},{" "}
              {selectedCityData?.city_details?.state}
            </h1>
          </div>
          <div key={selectedCityData.id}>
            <div className="city-header-new-design">
              <button
                type="button"
                className="back-icon-inline"
                aria-label="Back"
                onClick={() => {
                  if (from === "search") navigate("/search");
                  else if (from === "hospital" && pathname === "/full-map") {
                    navigate("/full-map");
                  } else if (from === "pieChart" || from === "dashboard") {
                    navigate("/dashboard");
                  } else navigate(-1);
                }}
              >
                <FiArrowLeft size={20} />
              </button>
              <div className="header-text-info">
                <div className="role-label">
                  {selectedCityData?.city_details?.role?.toUpperCase()}
                </div>
                <h1 className="city-state-title">
                  {selectedCityData?.city_details?.city},{" "}
                  {selectedCityData?.city_details?.state}
                </h1>
              </div>
              <div className="metrics-cards-container">
                {(() => {
                  const appDisplay = String(
                    selectedCityData?.city_details?.app_display || "",
                  )
                    .trim()
                    .toLowerCase();
                  let crimeVariant = "is-default";
                  if (appDisplay === "moderate") crimeVariant = "is-moderate";
                  else if (appDisplay === "mild") crimeVariant = "is-mild";
                  else if (appDisplay === "severe") crimeVariant = "is-severe";
                  return (
                    <div
                      className={`metric-card crime-card-new ${crimeVariant}`}
                      data-tour="cd-crime"
                    >
                      <div className="icon-wrapper">
                        <CrimeIcon />
                      </div>
                      <div className="metric-content">
                        <div className="metric-label">Crime</div>
                        <div className="metric-value">
                          <span className="crime-level">
                            {selectedCityData?.city_details?.app_display}
                          </span>
                          <span className="separator"> · </span>
                          <span className="crime-grade">
                            {
                              selectedCityData?.city_details
                                ?.overall_crime_grade
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div
                  className="metric-card hospital-card-new"
                  data-tour="cd-hospitals"
                  onClick={() => setIsHospitalsExpanded(true)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="icon-wrapper">
                    <HospitalsIcon />
                  </div>
                  <div className="metric-content">
                    <div className="metric-label">Hospitals</div>
                    <div className="metric-value">
                      <span className="hospital-count">
                        {selectedCityData?.city_details?.hospital_count}
                      </span>
                      <span className="nearby-text"> nearby</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hospital Details — mobile only, shown directly below the city header */}
            {(
              <div
                className="hospital-sidebar-card hospital-mobile-only"
                data-tour="cd-hosplist"
              >
                <div
                  className="job-card-title"
                  onClick={() =>
                    otherHospitals.length > 0 &&
                    setIsHospitalsExpanded(!isHospitalsExpanded)
                  }
                  style={
                    otherHospitals.length === 0
                      ? { cursor: "default" }
                      : undefined
                  }
                >
                  <span>
                    {hospitalCountLabel}
                    <InfoTooltip text="Hospitals in this city, closest first. Tap to expand, then click a hospital to open its own careers website in a new tab." />
                  </span>
                  {otherHospitals.length > 0 &&
                    (isHospitalsExpanded ? (
                      <FiChevronUp
                        size={20}
                        style={{ transition: "transform 0.3s ease" }}
                      />
                    ) : (
                      <FiChevronDown
                        size={20}
                        style={{ transition: "transform 0.3s ease" }}
                      />
                    ))}
                </div>
                <div className="hospital-body">
                  {!primaryHospital && (
                    <p className="no-hospitals">No Hospitals available</p>
                  )}
                  {primaryHospital && (
                    <div className="hospitals-container nearby-list">
                      <div
                        className="hospital-item"
                        onClick={() =>
                          handleHospitalCareersClick(
                            primaryHospital?.careers_url,
                          )
                        }
                        style={{
                          cursor: primaryHospital?.careers_url
                            ? "pointer"
                            : "default",
                        }}
                      >
                        <div className="content-wrap">
                          <div className="hospital-icon">
                            <HospitalIcon />
                          </div>
                          <div className="hospital-details">
                            <div className="hospital-name">
                              {primaryHospital?.hospital_name}
                            </div>
                            <div className="hospital-address">
                              {primaryHospital?.full_address}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {otherHospitals.length > 0 && (
                    <Collapse in={isHospitalsExpanded}>
                      <div>
                        <div className="hospitals-container nearby-list">
                          {otherHospitals.map((hospital, index) => (
                            <div
                              key={index}
                              className="hospital-item"
                              onClick={() =>
                                handleHospitalCareersClick(
                                  hospital?.careers_url,
                                )
                              }
                              style={{
                                cursor: hospital?.careers_url
                                  ? "pointer"
                                  : "default",
                              }}
                            >
                              <div className="content-wrap">
                                <div className="hospital-icon">
                                  <HospitalIcon />
                                </div>
                                <div className="hospital-details">
                                  <div className="hospital-name">
                                    {hospital?.hospital_name}
                                  </div>
                                  <div className="hospital-address">
                                    {hospital?.full_address}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Collapse>
                  )}
                </div>
              </div>
            )}

            <div className="action-buttons-group">
              <Button
                className={`btn-action-outline ${isSaved ? "is-saved" : ""}`}
                data-tour="cd-save"
                onClick={toggleSaveCity}
                disabled={savingBookmark}
              >
                <FiHeart
                  className="me-1"
                  fill={isSaved ? "currentColor" : "none"}
                />{" "}
                {isSaved ? "Saved" : "Save"}
              </Button>
              <Button className="btn-action-outline" data-tour="cd-compare">
                <CompareIcon className="me-1" /> Compare
              </Button>
              <Button
                className="btn-action-outline"
                data-tour="cd-share"
                onClick={openShareModal}
              >
                <ShareIcon className="me-1" /> Share
              </Button>
            </div>

            {(() => {
              const usage = selectedCityData?.usage;
              const planSlug = usage?.plan_slug;
              const profilePlan = profileStore?.profileItem?.data?.plan;
              const isPro =
                planSlug === "pro" ||
                planSlug === "plus" ||
                planSlug === "pro-newsletter" ||
                profilePlan?.is_paid === 1 ||
                usage?.comparisanMaxCount === -1;
              const planLabel = profilePlan?.name
                ? `${profilePlan.name} plan`
                : "Free plan";
              const usedCount = Number(usage?.used_count ?? 0);
              const maxCount = Number(usage?.comparisanMaxCount ?? 0);
              const bannerTitle = isPro
                ? `${planLabel}: Unlimited city details`
                : usage
                ? `${planLabel}: ${usedCount} of ${maxCount} city details viewed today`
                : `${planLabel}: city details usage`;
              const bannerSubtitle = isPro
                ? "Enjoy unlimited cities + advanced filters"
                : "Upgrade for unlimited cities + advanced filters";
              return (
                <div className="plan-usage-banner plan-banner-mobile-only">
                  <div className="banner-left">
                    <div className="banner-title">{bannerTitle}</div>
                    <div className="banner-subtitle">{bannerSubtitle}</div>
                  </div>
                  {!isPro && (
                    <Button
                      className="btn-upgrade"
                      onClick={() => navigate("/profile/subscription")}
                    >
                      Upgrade
                    </Button>
                  )}
                </div>
              );
            })()}

            <div className="salary-housing-row">
              <div className="city-content-right">
                {(
                  <div
                    className="hospital-sidebar-card hospital-desktop-only"
                    data-tour="cd-hosplist"
                  >
                    <div
                      className="job-card-title"
                      onClick={() =>
                        otherHospitals.length > 0 &&
                        setIsHospitalsExpanded(!isHospitalsExpanded)
                      }
                      style={
                        otherHospitals.length === 0
                          ? { cursor: "default" }
                          : undefined
                      }
                    >
                      <span>
                    {hospitalCountLabel}
                    <InfoTooltip text="Hospitals in this city, closest first. Tap to expand, then click a hospital to open its own careers website in a new tab." />
                  </span>
                      {otherHospitals.length > 0 &&
                        (isHospitalsExpanded ? (
                          <FiChevronUp
                            size={20}
                            style={{ transition: "transform 0.3s ease" }}
                          />
                        ) : (
                          <FiChevronDown
                            size={20}
                            style={{ transition: "transform 0.3s ease" }}
                          />
                        ))}
                    </div>
                    <div className="hospital-body">
                      {!primaryHospital && (
                        <p className="no-hospitals">No Hospitals available</p>
                      )}
                      {primaryHospital && (
                        <div className="hospitals-container nearby-list">
                          <div
                            className="hospital-item"
                            onClick={() =>
                              handleHospitalCareersClick(
                                primaryHospital?.careers_url,
                              )
                            }
                            style={{
                              cursor: primaryHospital?.careers_url
                                ? "pointer"
                                : "default",
                            }}
                          >
                            <div className="content-wrap">
                              <div className="hospital-icon">
                                <HospitalIcon />
                              </div>
                              <div className="hospital-details">
                                <div className="hospital-name">
                                  {primaryHospital?.hospital_name}
                                </div>
                                <div className="hospital-address">
                                  {primaryHospital?.full_address}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {otherHospitals.length > 0 && (
                        <Collapse in={isHospitalsExpanded}>
                          <div>
                            <div className="hospitals-container nearby-list">
                              {otherHospitals.map((hospital, index) => (
                                <div
                                  key={index}
                                  className="hospital-item"
                                  onClick={() =>
                                    handleHospitalCareersClick(
                                      hospital?.careers_url,
                                    )
                                  }
                                  style={{
                                    cursor: hospital?.careers_url
                                      ? "pointer"
                                      : "default",
                                  }}
                                >
                                  <div className="content-wrap">
                                    <div className="hospital-icon">
                                      <HospitalIcon />
                                    </div>
                                    <div className="hospital-details">
                                      <div className="hospital-name">
                                        {hospital?.hospital_name}
                                      </div>
                                      <div className="hospital-address">
                                        {hospital?.full_address}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Collapse>
                      )}
                    </div>
                  </div>
                )}
                <aside
                  className="quick-actions-panel"
                  aria-label="Quick actions"
                >
                  <div className="quick-actions-title">QUICK ACTIONS</div>
                  <button
                    type="button"
                    className="quick-action-row"
                    onClick={() =>
                      navigate("/full-map", {
                        state: {
                          fromCityDetail: {
                            city: selectedCityData?.city_details?.city || "",
                            state: selectedCityData?.city_details?.state || "",
                            role: selectedCityData?.city_details?.role || "",
                          },
                        },
                      })
                    }
                  >
                    <span className="qa-icon qa-icon-map">
                      <FiMapPin />
                    </span>
                    <span className="qa-label">View on map</span>
                  </button>
                  <button
                    type="button"
                    className="quick-action-row"
                    data-tour="cd-compare"
                  >
                    <span className="qa-icon qa-icon-compare">
                      <CompareIcon />
                    </span>
                    <span className="qa-label">Compare with another city</span>
                  </button>
                  <button
                    type="button"
                    className={`quick-action-row ${isSaved ? "is-saved" : ""}`}
                    data-tour="cd-save"
                    onClick={toggleSaveCity}
                    disabled={savingBookmark}
                  >
                    <span className="qa-icon qa-icon-save">
                      <FiHeart fill={isSaved ? "currentColor" : "none"} />
                    </span>
                    <span className="qa-label">
                      {isSaved ? "Saved to Your Cities" : "Save to Your Cities"}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="quick-action-row"
                    data-tour="cd-share"
                    onClick={openShareModal}
                  >
                    <span className="qa-icon qa-icon-share">
                      <ShareIcon />
                    </span>
                    <span className="qa-label">Share</span>
                  </button>
                </aside>
                {/* Job Cards Accordion */}
                <div className="job-card-collapse" data-tour="cd-jobs">
                  <div
                    className="job-card-title"
                    onClick={() => setIsJobsExpanded(!isJobsExpanded)}
                  >
                    <span>
                      Jobs for this city{" "}
                      {jobs?.length > 0 && `(${jobs.length})`}
                      <InfoTooltip text="Browse other jobs available in this city. Expand a listing to see a quick overview or select Apply to view the full job details." />
                    </span>
                    {isJobsExpanded ? (
                      <FiChevronUp
                        size={20}
                        style={{ transition: "transform 0.3s ease" }}
                      />
                    ) : (
                      <FiChevronDown
                        size={20}
                        style={{ transition: "transform 0.3s ease" }}
                      />
                    )}
                  </div>
                  <Collapse in={isJobsExpanded}>
                    <div>
                      <div className="jobs-body">
                        {jobsLoading ? (
                          <div
                            className="jobs-loading-container"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "40px 20px",
                              width: "100%",
                            }}
                          >
                            <div
                              className="spinner-border text-primary"
                              role="status"
                              style={{ width: "40px", height: "40px" }}
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p
                              style={{
                                marginTop: "12px",
                                color: "var(--mmp-text-secondary)",
                                fontSize: "14px",
                              }}
                            >
                              Loading jobs...
                            </p>
                          </div>
                        ) : jobs?.length === 0 ? (
                          <p className="no-jobs">No Jobs available</p>
                        ) : (
                          <>
                            <Row className="g-2">
                              {(showAllJobs ? jobs : jobs.slice(0, 4)).map(
                                (job) => (
                                  <Col
                                    key={job.id}
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    className="search-job-card"
                                  >
                                    <Jobcards
                                      job={job}
                                      onApply={(jobId) => {
                                        if (jobViewLimitDataRef.current) {
                                          setJobViewLimitInfo(
                                            jobViewLimitDataRef.current,
                                          );
                                          return;
                                        }
                                        navigate(`/job-detail/${jobId}`);
                                      }}
                                    />
                                  </Col>
                                ),
                              )}
                            </Row>
                            {jobs.length > 4 && !showAllJobs && (
                              <div className="view-more-jobs-wrap">
                                <button
                                  type="button"
                                  className="view-more-jobs-btn"
                                  onClick={() => setShowAllJobs(true)}
                                >
                                  View more jobs ({jobs.length - 4})
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </Collapse>
                </div>

                <div className="live-work-card">
                  <div className="live-work-header">
                    <span className="live-work-icon">
                      <FiMapPin />
                    </span>
                    <span className="live-work-title">
                      Live Here, Work There
                    </span>
                  </div>
                  <p className="live-work-subtitle">
                    Compare{" "}
                    {selectedCityData?.city_details?.city || "this city"}{" "}
                    against another city for relocation. Pre-filled as your work
                    city.
                  </p>
                  <div className="live-work-field">
                    <label>WORK</label>
                    <div className="live-work-input is-readonly">
                      {selectedCityData?.city_details?.city || ""}
                      {selectedCityData?.city_details?.state
                        ? `, ${selectedCityData.city_details.state}`
                        : ""}
                    </div>
                  </div>
                  <div className="live-work-field">
                    <label>LIVE</label>
                    <button
                      ref={liveLocTriggerRef}
                      type="button"
                      className={`live-work-input live-work-loc-trigger ${
                        liveSelectedCity || liveSelectedState
                          ? ""
                          : "is-placeholder"
                      }`}
                      onClick={() => setLiveLocOpen((v) => !v)}
                      aria-haspopup="listbox"
                      aria-expanded={liveLocOpen}
                    >
                      <span>
                        {liveSelectedCity?.city
                          ? `${liveSelectedCity.city}${
                              liveSelectedState?.state
                                ? `, ${liveSelectedState.state}`
                                : ""
                            }`
                          : liveSelectedState
                          ? liveSelectedState.state_full_name ||
                            liveSelectedState.state
                          : "Choose a city"}
                      </span>
                      <FiChevronDown
                        style={{
                          transform: liveLocOpen ? "rotate(180deg)" : "none",
                          transition: "transform 0.15s ease",
                        }}
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="live-work-btn"
                    onClick={() => {
                      const workStateCode =
                        selectedCityData?.city_details?.state || "";
                      const workStateMatch = (liveAllStates || []).find(
                        (s) => s.state === workStateCode,
                      );
                      const workStateFullName =
                        workStateMatch?.state_full_name || workStateCode;
                      const roleName =
                        selectedCityData?.city_details?.role || "";

                      navigate("/comparison", {
                        state: {
                          NursingRole: roleName
                            ? { value: roleName, label: roleName }
                            : undefined,
                          WorkState: workStateCode
                            ? {
                                value: workStateCode,
                                label: workStateFullName,
                              }
                            : undefined,
                          WorkCity: selectedCityData?.city_details?.city
                            ? {
                                value: selectedCityData.city_details.city,
                                label: selectedCityData.city_details.city,
                              }
                            : undefined,
                          LiveState: liveSelectedState
                            ? {
                                value: liveSelectedState.state,
                                label:
                                  liveSelectedState.state_full_name ||
                                  liveSelectedState.state,
                              }
                            : undefined,
                          LiveCity: liveSelectedCity?.city
                            ? {
                                value: liveSelectedCity.city,
                                label: liveSelectedCity.city,
                              }
                            : undefined,
                        },
                      });
                    }}
                  >
                    {/* Open full tool → */}
                    Check Live Here, Work There
                  </button>

                  {liveLocOpen &&
                    liveLocPos &&
                    createPortal(
                      <div
                        ref={liveLocPopoverRef}
                        className="live-work-loc-popover"
                        style={{
                          position: "fixed",
                          top: liveLocPos.top,
                          left: liveLocPos.left,
                          width: liveLocPos.width,
                          maxHeight: liveLocPos.maxHeight || 320,
                          zIndex: 9500,
                        }}
                        role="listbox"
                      >
                        {(liveSelectedState || liveSelectedCity) && (
                          <button
                            type="button"
                            className="live-loc-clear"
                            onClick={() => {
                              setLiveSelectedState(null);
                              setLiveSelectedCity(null);
                              setLiveLocOpen(false);
                            }}
                          >
                            Clear all
                          </button>
                        )}
                        {!liveSelectedState && (
                          <>
                            <input
                              type="text"
                              className="live-loc-search"
                              value={liveStateSearch}
                              onChange={(e) =>
                                setLiveStateSearch(e.target.value)
                              }
                              placeholder="Search state…"
                              autoFocus
                            />
                            {(!liveAllStates || liveAllStates.length === 0) && (
                              <div className="live-loc-option is-empty">
                                Loading…
                              </div>
                            )}
                            {liveAllStates &&
                              liveAllStates.length > 0 &&
                              (() => {
                                const q = liveStateSearch.trim().toLowerCase();
                                const filtered = liveAllStates.filter((s) => {
                                  if (!q) return true;
                                  return (
                                    (s.state_full_name || "")
                                      .toLowerCase()
                                      .includes(q) ||
                                    (s.state || "").toLowerCase().includes(q)
                                  );
                                });
                                if (filtered.length === 0) {
                                  return (
                                    <div className="live-loc-option is-empty">
                                      No states found
                                    </div>
                                  );
                                }
                                return filtered.map((s) => (
                                  <button
                                    key={s.state}
                                    type="button"
                                    className="live-loc-option"
                                    onClick={() => {
                                      setLiveSelectedState(s);
                                      setLiveSelectedCity(null);
                                    }}
                                    role="option"
                                  >
                                    {s.state_full_name || s.state}
                                  </button>
                                ));
                              })()}
                          </>
                        )}

                        {liveSelectedState && (
                          <>
                            <div className="live-loc-header">
                              <span>
                                {liveSelectedState.state_full_name ||
                                  liveSelectedState.state}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setLiveSelectedState(null);
                                  setLiveSelectedCity(null);
                                }}
                              >
                                Change
                              </button>
                            </div>
                            <input
                              type="text"
                              className="live-loc-search"
                              value={liveCitySearch}
                              onChange={(e) =>
                                setLiveCitySearch(e.target.value)
                              }
                              placeholder="Search city…"
                              autoFocus
                            />
                            {liveCitiesLoading && (
                              <div className="live-loc-option is-empty">
                                Loading…
                              </div>
                            )}
                            {!liveCitiesLoading &&
                              (() => {
                                const q = liveCitySearch.trim().toLowerCase();
                                const filtered = (liveAllCities || []).filter(
                                  (c) => {
                                    if (!q) return true;
                                    return (c.city || "")
                                      .toLowerCase()
                                      .includes(q);
                                  },
                                );
                                if (filtered.length === 0) {
                                  return (
                                    <div className="live-loc-option is-empty">
                                      No cities found
                                    </div>
                                  );
                                }
                                return filtered.map((c, i) => {
                                  const isSel =
                                    liveSelectedCity &&
                                    liveSelectedCity.city === c.city;
                                  return (
                                    <button
                                      key={`${c.city}-${i}`}
                                      type="button"
                                      className={`live-loc-option ${
                                        isSel ? "is-selected" : ""
                                      }`}
                                      onClick={() => {
                                        setLiveSelectedCity(c);
                                        setLiveLocOpen(false);
                                      }}
                                      role="option"
                                      aria-selected={isSel}
                                    >
                                      {c.city}
                                    </button>
                                  );
                                });
                              })()}
                          </>
                        )}
                      </div>,
                      document.body,
                    )}
                </div>
              </div>

              <div className="city-content-left">
                <div className="salary-housing-section" data-tour="cd-salary">
                  <div className="salary-housing-top">
                    <div className="salary-housing-title">Salary & Housing</div>
                  </div>

                  <div className="salary-home-grid">
                    <div className="salary-home-card">
                      <div className="card-label">
                        Gross avg salary in{" "}
                        {selectedCityData?.city_details?.city},{" "}
                        {selectedCityData?.city_details?.state}
                        <InfoTooltip text="The median gross salary for this role in this city, shown as a yearly and hourly rate." />
                      </div>
                      <div className="card-price">
                        {selectedCityData?.city_details?.median_annual_salary
                          ? `$${(
                              Number(
                                selectedCityData.city_details
                                  .median_annual_salary,
                              ) / 1000
                            ).toFixed(1)}K`
                          : "$0"}
                      </div>
                      <div className="card-subtext">
                        $
                        {selectedCityData?.city_details?.gross_hourly_salary
                          ? Number(
                              selectedCityData.city_details.gross_hourly_salary,
                            ).toFixed(1)
                          : "0"}
                        /hr
                      </div>
                    </div>

                    <div className="salary-home-card">
                      <div className="card-label">
                        Typical Home Value for the Region
                        <InfoTooltip text="The median price of a single-family home in this city." />
                      </div>
                      <div className="card-price">
                        {(() => {
                          const n = Number(
                            selectedCityData?.city_details?.home_prices || 0,
                          );
                          if (n <= 0) return "$0";
                          if (n >= 1_000_000)
                            return `$${(n / 1_000_000).toFixed(2)}M`;
                          return `$${Math.round(n / 1000).toLocaleString(
                            "en-US",
                          )}K`;
                        })()}
                      </div>
                      <div className="card-subtext">single-family</div>
                    </div>
                  </div>

                  <div className="leftover-balance-card">
                    <div className="leftover-title">LEFT OVER EACH MONTH</div>
                    <div className="leftover-grid">
                      <div className="leftover-item">
                        <div className="leftover-label">
                          Owning
                          <InfoTooltip text="Your salary left over each month after deducting the mortgage and other monthly expenses." />
                        </div>
                        <div
                          className={`leftover-price ${
                            liveOwningLeftoverMonthly < 0 ? "negative" : ""
                          }`}
                        >
                          $
                          {Number(
                            Math.round(liveOwningLeftoverMonthly || 0),
                          ).toLocaleString("en-US")}
                        </div>
                      </div>
                      <div className="leftover-item border-left">
                        <div className="leftover-label">
                          Renting
                          <InfoTooltip text="Your salary left over each month after deducting rent and other monthly expenses." />
                        </div>
                        <div
                          className={`leftover-price ${
                            Number(
                              selectedCityData?.city_details
                                ?.left_over_annually_if_renting,
                            ) < 0
                              ? "negative"
                              : ""
                          }`}
                        >
                          $
                          {selectedCityData?.city_details
                            ?.left_over_annually_if_renting
                            ? Number(
                                (
                                  selectedCityData.city_details
                                    .left_over_annually_if_renting / 12
                                ).toFixed(0),
                              ).toLocaleString("en-US")
                            : "0"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="money-month">
                    <div className="money-month-title">
                      Where your money goes each month
                      <InfoTooltip text="See how your estimated monthly pay is divided between income taxes, housing costs, and your remaining take-home pay." />
                    </div>
                    <div className="money-month-wrapper">
                      <div className="monthly-graph">
                        <div className="chart-wrap">
                          <Pie
                            data={liveChartPieData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { display: false },
                                tooltip: { enabled: true },
                              },
                              cutout: "50%",
                            }}
                          />
                        </div>
                      </div>

                      <div className="money-month-legend">
                        {[
                          {
                            label: "Income Tax",
                            key: "taxes",
                            percent: formatPercent(
                              selectedCityData?.pie_data?.[0]?.text,
                            ).toFixed(0),
                            value:
                              selectedCityData?.city_details?.monthly_tax_paid,
                          },
                          {
                            label: "Mortgage",
                            key: "mortgage",
                            percent: affordCalcLive
                              ? liveDonutMortgagePct.toFixed(0)
                              : formatPercent(
                                  selectedCityData?.pie_data?.[1]?.text,
                                ).toFixed(0),
                            value: liveDonutMortgageValue,
                          },
                          {
                            label: "Left Over",
                            key: "leftover",
                            percent: affordCalcLive
                              ? liveDonutLeftOverPct.toFixed(0)
                              : Number(
                                  selectedCityData?.city_details
                                    ?.left_over_percent ?? 0,
                                ).toFixed(0),
                            value: liveDonutLeftOverValue,
                          },
                        ].map((item, idx) => (
                          <div className="item" key={idx}>
                            <div className="left">
                              <span className={`key ${item.key}`}></span>
                              <div className="text">
                                <div className="label">{item.label}</div>
                                <div className="percentage">
                                  {item.percent}%
                                </div>
                              </div>
                            </div>
                            <div className="price">
                              $
                              {item.value
                                ? Number(
                                    Number(item.value).toFixed(0),
                                  ).toLocaleString("en-US")
                                : "0"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {(() => {
                  const usage = selectedCityData?.usage;
                  const planSlug = usage?.plan_slug;
                  const profilePlan = profileStore?.profileItem?.data?.plan;
                  const isPro =
                    planSlug === "pro" ||
                    planSlug === "plus" ||
                    planSlug === "pro-newsletter" ||
                    profilePlan?.is_paid === 1 ||
                    usage?.comparisanMaxCount === -1;
                  const planLabel = profilePlan?.name
                    ? `${profilePlan.name} plan`
                    : "Free plan";
                  const usedCount = Number(usage?.used_count ?? 0);
                  const maxCount = Number(usage?.comparisanMaxCount ?? 0);
                  const bannerTitle = isPro
                    ? `${planLabel}: Unlimited city details`
                    : usage
                    ? `${planLabel}: ${usedCount} of ${maxCount} city details viewed today`
                    : `${planLabel}: city details usage`;
                  const bannerSubtitle = isPro
                    ? "Enjoy unlimited cities + advanced filters"
                    : "Upgrade for unlimited cities + advanced filters";
                  return (
                    <div className="plan-usage-banner plan-banner-desktop-only">
                      <div className="banner-left">
                        <div className="banner-title">{bannerTitle}</div>
                        <div className="banner-subtitle">{bannerSubtitle}</div>
                      </div>
                      {!isPro && (
                        <Button
                          className="btn-upgrade"
                          onClick={() => navigate("/profile/subscription")}
                        >
                          Upgrade
                        </Button>
                      )}
                    </div>
                  );
                })()}

                <div className="subscription-details affordability-calculator-section">
                  <div className="custom-accordion p-0">
                    <div className="custom-accordion-item">
                      <button
                        className="custom-accordion-button affordability-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#affordability-collapse-${id}`}
                        aria-expanded="true"
                        aria-controls={`affordability-collapse-${id}`}
                      >
                        <div className="title-area">
                          <span className="main-title">
                            Can I afford to buy here?
                            <InfoTooltip text="Estimates mortgage, down payment, and rent-vs-own costs for this city based on the inputs below." />
                          </span>
                          <span className="sub-title">
                            Mortgage, down payment, and rent vs own
                          </span>
                        </div>
                        <svg
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <div
                        id={`affordability-collapse-${id}`}
                        className="collapse show"
                      >
                        <div className="affordability-content" data-tour="cd-afford">
                          <div className="subscription-tabs d-flex gap-2">
                            <div className="tab-segment" data-tour="cd-aff-mode">
                              <button
                                type="button"
                                className={`tab-pill ${
                                  activePlan === "For Sale" ? "active" : ""
                                }`}
                                onClick={() => {
                                  setActivePlan("For Sale");
                                  setSelectedPlan("For Sale");
                                }}
                              >
                                For Sale
                              </button>

                              <button
                                type="button"
                                className={`tab-pill ${
                                  activePlan === "For Rent" ? "active" : ""
                                }`}
                                onClick={() => {
                                  setActivePlan("For Rent");
                                  setSelectedPlan("For Rent");
                                }}
                              >
                                For Rent
                              </button>
                            </div>

                            <div
                              className={`tab-pill p-0 ${
                                activePlan === "Distance" ? "active" : ""
                              }`}
                              data-tour="cd-radius"
                            >
                              <Select
                                className="distance-select"
                                classNamePrefix="distance"
                                options={distanceOptions}
                                value={distanceOptions.find(
                                  (o) => o.value === distance,
                                )}
                                onChange={(option) => {
                                  setDistance(option.value);
                                  setSelectedPlan(`${option.value} mi`);
                                }}
                                isSearchable={false}
                                menuPortalTarget={
                                  typeof document !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "32px",
                                    height: "32px",
                                    border: "none",
                                    boxShadow: "none",
                                    cursor: "pointer",
                                  }),
                                  valueContainer: (base) => ({
                                    ...base,
                                    padding: "0 4px 0 10px",
                                    height: "32px",
                                  }),
                                  singleValue: (base) => ({
                                    ...base,
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "var(--mmp-text-primary)",
                                    margin: 0,
                                  }),
                                  input: (base) => ({
                                    ...base,
                                    margin: 0,
                                    padding: 0,
                                  }),
                                  dropdownIndicator: (base) => ({
                                    ...base,
                                    padding: "0 8px 0 0",
                                    color: "var(--mmp-text-secondary)",
                                  }),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                    minWidth: "90px",
                                    width: "max-content",
                                    right: 0,
                                    left: "auto",
                                    backgroundColor: "var(--mmp-surface)",
                                  }),
                                  menuList: (base) => ({
                                    ...base,
                                    padding: "4px",
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                    "&::-webkit-scrollbar": {
                                      display: "none",
                                      width: 0,
                                      height: 0,
                                    },
                                  }),
                                  option: (base, state) => ({
                                    ...base,
                                    borderRadius: "8px",
                                    padding: "4px 12px",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                    backgroundColor: state.isFocused
                                      ? "var(--mmp-accent-bg)"
                                      : "var(--mmp-surface)",
                                    color: "var(--mmp-text-primary)",
                                    cursor: "pointer",
                                    "&:active": {
                                      backgroundColor: "var(--mmp-accent-bg)",
                                    },
                                  }),
                                  indicatorSeparator: () => ({
                                    display: "none",
                                  }),
                                }}
                              />
                            </div>
                          </div>

                          {activePlan === "For Sale" && (
                            <>
                              <div className="calculator-form">
                                <div className="form-group" data-tour="cd-price">
                                  <label>
                                    Typical Home Value for the Region
                                    <InfoTooltip text="Pre-filled with the median single-family home price in this city — edit it to model a different home value." />
                                  </label>
                                  <div className="input-with-icon">
                                    <span className="icon">$</span>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={
                                        homePrice > 0
                                          ? Number(homePrice).toLocaleString(
                                              "en-US",
                                            )
                                          : ""
                                      }
                                      placeholder="0"
                                      maxLength={10}
                                      onChange={(e) => {
                                        const digits = e.target.value.replace(
                                          /[^0-9]/g,
                                          "",
                                        );
                                        setHomePrice(
                                          digits === "" ? 0 : Number(digits),
                                        );
                                        markAffordEdited();
                                      }}
                                    />
                                  </div>
                                </div>

                                <div
                                  className="form-group slider-group"
                                  data-tour="cd-down"
                                >
                                  <div className="label-row">
                                    <label>DOWN PAYMENT</label>
                                    <span className="value">
                                      {downPaymentPct}% · $
                                      {homePrice > 0
                                        ? Number(
                                            (Number(homePrice) *
                                              downPaymentPct) /
                                              100,
                                          ).toLocaleString("en-US", {
                                            maximumFractionDigits: 0,
                                          })
                                        : "0"}
                                    </span>
                                  </div>
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={downPaymentPct}
                                    onChange={(e) => {
                                      setDownPaymentPct(Number(e.target.value));
                                      markAffordEdited();
                                    }}
                                    className="custom-range"
                                    style={{ "--val": `${downPaymentPct}%` }}
                                  />
                                </div>

                                <div className="form-group" data-tour="cd-term">
                                  <label>LOAN TERM</label>
                                  <div className="term-tabs">
                                    <button
                                      className={
                                        loanTerm === 15 ? "active" : ""
                                      }
                                      onClick={() => {
                                        setLoanTerm(15);
                                        markAffordEdited();
                                      }}
                                    >
                                      15 yr
                                    </button>
                                    <button
                                      className={
                                        loanTerm === 30 ? "active" : ""
                                      }
                                      onClick={() => {
                                        setLoanTerm(30);
                                        markAffordEdited();
                                      }}
                                    >
                                      30 yr
                                    </button>
                                  </div>
                                </div>

                                <div className="form-group" data-tour="cd-rate">
                                  <label>INTEREST RATE</label>
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={interestRate}
                                      onChange={(e) => {
                                        setInterestRate(Number(e.target.value));
                                        markAffordEdited();
                                      }}
                                    />
                                    <span className="suffix">% APR</span>
                                  </div>
                                </div>
                              </div>

                              <div className="results-cards">
                                <div className="result-card">
                                  <div
                                    className="label"
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>EST. MORTGAGE</span>
                                    <InfoTooltip text="Estimated from the home price, down payment, interest rate, and loan term above." />
                                  </div>
                                  <div className="value blue-val">
                                    $
                                    {Number(
                                      affordMonthlyMortgage.toFixed(0),
                                    ).toLocaleString("en-US")}
                                  </div>
                                  <div className="suffix">/month</div>
                                </div>
                                <div className="result-card utilities-card">
                                  <div
                                    className="label"
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>EXPENSES</span>
                                    <InfoTooltip text="Estimated monthly utilities and other living costs. Tap the amount to edit it." />
                                  </div>
                                  <div
                                    className="value utilities-display"
                                    onClick={openUtilitiesEditor}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openUtilitiesEditor();
                                      }
                                    }}
                                  >
                                    <span className="utilities-amount">
                                      $
                                      {Number(
                                        Number(affordUtilities) || 0,
                                      ).toLocaleString("en-US")}
                                    </span>
                                    <Edit2
                                      size={13}
                                      className="utilities-edit-icon"
                                      aria-label="Edit utilities"
                                    />
                                  </div>
                                  <div className="suffix">/month</div>
                                </div>
                                <div className="result-card">
                                  <div
                                    className="label"
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>LEFTOVER</span>
                                    <InfoTooltip text="Net pay minus the mortgage and expenses." />
                                  </div>
                                  <div
                                    className={`value ${
                                      affordLeftoverMonthly < 0
                                        ? "red-val"
                                        : "green-val"
                                    }`}
                                  >
                                    $
                                    {Number(
                                      affordLeftoverMonthly.toFixed(0),
                                    ).toLocaleString("en-US")}
                                  </div>
                                  <div className="suffix">/month</div>
                                </div>
                              </div>

                              <div
                                className={`warning-banner afford-tier-${affordTier}`}
                                data-tour="cd-aff-bar"
                              >
                                <div className="text-row">
                                  <span className="bold-text">
                                    Mortgage takes up{" "}
                                    {affordPctOfTakeHome.toFixed(0)}% of
                                    take-home
                                    <InfoTooltip text="Under 28% is likely affordable, 28-35% stretches the budget, and 35%+ is likely unaffordable." />
                                  </span>
                                  <span className="status">{affordLabel}</span>
                                </div>
                                <div className="progress-bar-container">
                                  <div
                                    className="progress-fill"
                                    style={{ width: `${affordPctClamped}%` }}
                                  ></div>
                                </div>
                              </div>

                              <div className="rent-vs-own-summary">
                                <h6>
                                  Renting vs Owning
                                  <InfoTooltip text="Compares the median rent in this city against the estimated mortgage above." />
                                </h6>
                                <div className="comparison">
                                  <div className="side">
                                    <div className="lbl">Rent</div>
                                    <div className="amt">
                                      $
                                      {selectedCityData?.city_details
                                        ?.average_monthly_rent_prices
                                        ? Number(
                                            selectedCityData.city_details
                                              .average_monthly_rent_prices,
                                          ).toLocaleString("en-US", {
                                            maximumFractionDigits: 0,
                                          })
                                        : "0"}
                                      <span>/mo</span>
                                    </div>
                                  </div>
                                  <div className="side">
                                    <div className="lbl">Own</div>
                                    <div className="amt">
                                      $
                                      {Number(
                                        affordMonthlyMortgage.toFixed(0),
                                      ).toLocaleString("en-US")}
                                      <span>/mo</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="conclusion">
                                  {affordOwnVsRentDiff >= 0
                                    ? "Owning costs "
                                    : "Renting costs "}
                                  <span>
                                    $
                                    {Number(
                                      Math.abs(affordOwnVsRentDiff).toFixed(0),
                                    ).toLocaleString("en-US")}
                                  </span>{" "}
                                  more per month here.
                                </div>
                              </div>
                            </>
                          )}

                          {activePlan === "For Rent" && (
                            <>
                              <div className="results-cards">
                                <div className="result-card">
                                  <div
                                    className="label"
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>MEDIAN RENT</span>
                                    <InfoTooltip text="City median monthly rent for this area." />
                                  </div>
                                  <div className="value">
                                    $
                                    {Number(
                                      affordMonthlyRent.toFixed(0),
                                    ).toLocaleString("en-US")}
                                  </div>
                                  <div className="suffix">/month</div>
                                </div>
                                <div className="result-card utilities-card">
                                  <div
                                    className="label"
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>UTILITIES</span>
                                    <InfoTooltip text="Estimated monthly utilities and other living costs. Tap the amount to edit it." />
                                  </div>
                                  <div
                                    className="value utilities-display"
                                    onClick={openUtilitiesEditor}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openUtilitiesEditor();
                                      }
                                    }}
                                  >
                                    <span className="utilities-amount">
                                      $
                                      {Number(
                                        Number(affordUtilities) || 0,
                                      ).toLocaleString("en-US")}
                                    </span>
                                    <Edit2
                                      size={13}
                                      className="utilities-edit-icon"
                                      aria-label="Edit utilities"
                                    />
                                  </div>
                                  <div className="suffix">/month</div>
                                </div>
                                <div className="result-card">
                                  <div
                                    className="label"
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>LEFTOVER</span>
                                    <InfoTooltip text="Net pay minus median rent and expenses." />
                                  </div>
                                  <div
                                    className={`value ${
                                      affordRentLeftover < 0
                                        ? "red-val"
                                        : "green-val"
                                    }`}
                                  >
                                    $
                                    {Number(
                                      affordRentLeftover.toFixed(0),
                                    ).toLocaleString("en-US")}
                                  </div>
                                  <div className="suffix">/month</div>
                                </div>
                              </div>

                              <div className="rent-vs-own-summary rent-narrative-banner">
                                <div className="conclusion">
                                  Renting in {city || "this city"} averages{" "}
                                  <span>
                                    $
                                    {Number(
                                      affordRentAllIn.toFixed(0),
                                    ).toLocaleString("en-US")}
                                    /mo
                                  </span>{" "}
                                  all-in for a 1-bed unit, leaving{" "}
                                  <span>
                                    $
                                    {Number(
                                      affordRentLeftover.toFixed(0),
                                    ).toLocaleString("en-US")}
                                  </span>{" "}
                                  after taxes
                                  {affordRole
                                    ? ` from your ${affordRole.toLowerCase()} salary`
                                    : ""}
                                  .
                                </div>
                              </div>
                            </>
                          )}

                          <div className="action-buttons" data-tour="cd-viewhomes">
                            <Button
                              className="redfin-btn"
                              onClick={() =>
                                window.open(
                                  currentData.buttons.redfin.link,
                                  "_blank",
                                )
                              }
                            >
                              {activePlan === "For Rent"
                                ? "View rentals on Redfin"
                                : "View homes for sale on Redfin"}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10 3.5h3.5v3.5M13.5 3.5l-6 6M6 3.5H2.5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V8" />
                              </svg>
                            </Button>
                            <Button
                              className="zillow-btn"
                              onClick={searchOnZillow}
                            >
                              View homes for{" "}
                              {activePlan === "For Rent" ? "rent" : "sale"} on
                              Zillow
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10 3.5h3.5v3.5M13.5 3.5l-6 6M6 3.5H2.5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V8" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* <SearchDetailWork /> */}

                {selectedCityData?.city_details?.role ===
                  "Registered Nurses" && (
                  <>
                    <div
                      className={`license-req-desktop-card is-license-req-card ${
                        isLicenseReqExpanded ? "is-open" : "is-closed"
                      }`}
                      data-tour="cd-lic-requirements"
                    >
                      <button
                        type="button"
                        className="license-req-card-title"
                        onClick={() => setIsLicenseReqExpanded((v) => !v)}
                        aria-expanded={isLicenseReqExpanded}
                      >
                        <span>
                          License Requirements
                          <InfoTooltip text="State licensing fees and requirements for Registered Nurses working here." />
                        </span>
                        <svg
                          className="license-req-chevron"
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <Collapse in={isLicenseReqExpanded}>
                        <div>
                          <div className="license-req-card-body">
                            <div className="info-row">
                              <span className="label">Compact state</span>
                              <span
                                className={`value fw-bold ${
                                  selectedCityData?.nursing_license_req?.Compact_State?.toLowerCase().includes(
                                    "no",
                                  )
                                    ? "text-danger"
                                    : ""
                                }`}
                              >
                                {selectedCityData?.nursing_license_req?.Compact_State?.toLowerCase().includes(
                                  "no",
                                )
                                  ? "No"
                                  : "Yes"}
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="label">Initial license fee</span>
                              <span className="value fw-bold">
                                $
                                {selectedCityData?.nursing_license_req
                                  ?.Initial_License_Fee || "N/A"}
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="label">
                                NCLEX registration fee
                              </span>
                              <span className="value fw-bold">
                                $
                                {selectedCityData?.nursing_license_req
                                  ?.NCLEX_Registration_Fee || "N/A"}
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="label">
                                Fingerprinting Requirements
                              </span>
                              <span className="value fw-bold">
                                $
                                {selectedCityData?.nursing_license_req
                                  ?.Fingerprinting_Requirements || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Collapse>
                    </div>

                    <div
                      className={`license-req-desktop-card ${
                        isCeusExpanded ? "is-open" : "is-closed"
                      }`}
                      data-tour="cd-lic-ceus"
                    >
                      <button
                        type="button"
                        className="license-req-card-title"
                        onClick={() => setIsCeusExpanded((v) => !v)}
                        aria-expanded={isCeusExpanded}
                      >
                        <span>
                          CEUs
                          <InfoTooltip text="Continuing education hours required to renew your nursing license, and how often." />
                        </span>
                        <svg
                          className="license-req-chevron"
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <Collapse in={isCeusExpanded}>
                        <div>
                          <div className="license-req-card-body">
                            {selectedCityData?.nursing_license_req
                              ?.CEU_hours_required && (
                              <div className="info-row">
                                <span className="label">
                                  CEU hours required
                                </span>
                                <span className="value fw-bold">
                                  {
                                    selectedCityData.nursing_license_req
                                      .CEU_hours_required
                                  }
                                </span>
                              </div>
                            )}
                            {selectedCityData?.nursing_license_req
                              ?.Renewal_period && (
                              <div className="info-row">
                                <span className="label">Renewal period</span>
                                <span className="value fw-bold">
                                  {
                                    selectedCityData.nursing_license_req
                                      .Renewal_period
                                  }
                                </span>
                              </div>
                            )}
                            <div className="paragraph-text mt-2">
                              {selectedCityData?.nursing_license_req?.CEUs ||
                                "N/A"}
                            </div>
                          </div>
                        </div>
                      </Collapse>
                    </div>

                    <div
                      className={`license-req-desktop-card ${
                        isSpecialReqExpanded ? "is-open" : "is-closed"
                      }`}
                      data-tour="cd-lic-special"
                    >
                      <button
                        type="button"
                        className="license-req-card-title"
                        onClick={() => setIsSpecialReqExpanded((v) => !v)}
                        aria-expanded={isSpecialReqExpanded}
                      >
                        <span>
                          Special Requirements
                          <InfoTooltip text="Additional state-mandated training required for licensure, such as implicit bias or abuse-prevention courses." />
                        </span>
                        <svg
                          className="license-req-chevron"
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <Collapse in={isSpecialReqExpanded}>
                        <div>
                          <div className="license-req-card-body">
                            <div className="paragraph-text">
                              {selectedCityData?.nursing_license_req
                                ?.Special_Requirements || "N/A"}
                            </div>
                          </div>
                        </div>
                      </Collapse>
                    </div>

                    <div
                      className={`license-req-desktop-card ${
                        isCompactStateExpanded ? "is-open" : "is-closed"
                      }`}
                      data-tour="cd-lic-compact"
                    >
                      <button
                        type="button"
                        className="license-req-card-title"
                        onClick={() => setIsCompactStateExpanded((v) => !v)}
                        aria-expanded={isCompactStateExpanded}
                      >
                        <span>
                          Compact State
                          <InfoTooltip text="Whether this state participates in the Nurse Licensure Compact, letting multistate license holders practice here without a separate license." />
                        </span>
                        <svg
                          className="license-req-chevron"
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <Collapse in={isCompactStateExpanded}>
                        <div>
                          <div className="license-req-card-body">
                            {selectedCityData?.nursing_license_req?.Compact_State?.toLowerCase().includes(
                              "no",
                            ) ? (
                              <div className="compact-state-alert">
                                <span className="fw-bold text-danger">No</span>{" "}
                                —{" "}
                                {selectedCityData?.city_details?.state ||
                                  "This state"}{" "}
                                does not participate in the Nurse Licensure
                                Compact.
                              </div>
                            ) : (
                              <div className="paragraph-text">
                                {selectedCityData?.nursing_license_req
                                  ?.Compact_State || "N/A"}
                              </div>
                            )}
                          </div>
                        </div>
                      </Collapse>
                    </div>

                    {selectedCityData?.nursing_license_req
                      ?.Important_Considerations_for_Compact_State_Nurses && (
                      <div
                        className={`license-req-desktop-card ${
                          isImportantConsiderationsExpanded
                            ? "is-open"
                            : "is-closed"
                        }`}
                        data-tour="cd-lic-considerations"
                      >
                        <button
                          type="button"
                          className="license-req-card-title"
                          onClick={() =>
                            setIsImportantConsiderationsExpanded((v) => !v)
                          }
                          aria-expanded={isImportantConsiderationsExpanded}
                        >
                          <span>
                            Important Considerations for Compact State Nurses
                            <InfoTooltip text="Extra guidance for nurses relocating here on a multistate compact license." />
                          </span>
                          <svg
                            className="license-req-chevron"
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                            fill="none"
                          >
                            <path
                              d="M1 6.5L6 1.5L11 6.5"
                              stroke="var(--mmp-text-secondary)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <Collapse in={isImportantConsiderationsExpanded}>
                          <div>
                            <div className="license-req-card-body">
                              <div
                                className="paragraph-text"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    selectedCityData?.nursing_license_req
                                      ?.Important_Considerations_for_Compact_State_Nurses ||
                                    "N/A",
                                }}
                              />
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    )}
                    <div
                      className="demographics-content  licensing-compliance-section"
                      data-tour="cd-licensing"
                    >
                      <div className="custom-accordion p-0">
                        <div className="custom-accordion-item main-accordion">
                          <button
                            className="custom-accordion-button main-accordion-btn"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#licensing-compliance-${id}`}
                            aria-expanded="true"
                            aria-controls={`licensing-compliance-${id}`}
                          >
                            <span className="main-title">
                              Licensing & Compliance
                            </span>
                            <svg
                              width="12"
                              height="8"
                              viewBox="0 0 12 8"
                              fill="none"
                            >
                              <path
                                d="M1 6.5L6 1.5L11 6.5"
                                stroke="var(--mmp-text-secondary)"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>

                          <div
                            id={`licensing-compliance-${id}`}
                            className="collapse show"
                          >
                            <div className="sub-accordions-wrapper">
                              {/* License Requirements Sub-Accordion */}
                              <div className="sub-accordion-item">
                                <button
                                  className="sub-accordion-button"
                                  type="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#sub-license-req-${id}`}
                                  aria-expanded="true"
                                >
                                  <span>
                                    License Requirements
                                    <InfoTooltip text="State licensing fees and requirements for Registered Nurses working here." />
                                  </span>
                                  <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 12 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M1 6.5L6 1.5L11 6.5"
                                      stroke="var(--mmp-text-secondary)"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                <div
                                  id={`sub-license-req-${id}`}
                                  className="collapse show sub-collapse"
                                >
                                  <div className="sub-accordion-body">
                                    <div className="info-row">
                                      <span className="label">
                                        Compact state
                                      </span>
                                      <span
                                        className={`value fw-bold ${
                                          selectedCityData?.nursing_license_req?.Compact_State?.toLowerCase().includes(
                                            "no",
                                          )
                                            ? "text-danger"
                                            : ""
                                        }`}
                                      >
                                        {selectedCityData?.nursing_license_req?.Compact_State?.toLowerCase().includes(
                                          "no",
                                        )
                                          ? "No"
                                          : "Yes"}
                                      </span>
                                    </div>
                                    <div className="info-row">
                                      <span className="label">
                                        Initial license fee
                                      </span>
                                      <span className="value fw-bold">
                                        $
                                        {selectedCityData?.nursing_license_req
                                          ?.Initial_License_Fee || "N/A"}
                                      </span>
                                    </div>
                                    <div className="info-row">
                                      <span className="label">
                                        NCLEX registration fee
                                      </span>
                                      <span className="value fw-bold">
                                        $
                                        {selectedCityData?.nursing_license_req
                                          ?.NCLEX_Registration_Fee || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* CEUs Sub-Accordion */}
                              <div className="sub-accordion-item">
                                <button
                                  className="sub-accordion-button"
                                  type="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#sub-ceus-${id}`}
                                  aria-expanded="true"
                                >
                                  <span>
                                    CEUs
                                    <InfoTooltip text="Continuing education hours required to renew your nursing license, and how often." />
                                  </span>
                                  <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 12 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M1 6.5L6 1.5L11 6.5"
                                      stroke="var(--mmp-text-secondary)"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                <div
                                  id={`sub-ceus-${id}`}
                                  className="collapse show sub-collapse"
                                >
                                  <div className="sub-accordion-body">
                                    {selectedCityData?.nursing_license_req
                                      ?.CEU_hours_required && (
                                      <div className="info-row">
                                        <span className="label">
                                          CEU hours required
                                        </span>
                                        <span className="value fw-bold">
                                          {
                                            selectedCityData.nursing_license_req
                                              .CEU_hours_required
                                          }
                                        </span>
                                      </div>
                                    )}
                                    {selectedCityData?.nursing_license_req
                                      ?.Renewal_period && (
                                      <div className="info-row">
                                        <span className="label">
                                          Renewal period
                                        </span>
                                        <span className="value fw-bold">
                                          {
                                            selectedCityData.nursing_license_req
                                              .Renewal_period
                                          }
                                        </span>
                                      </div>
                                    )}
                                    <div className="paragraph-text mt-2">
                                      {selectedCityData?.nursing_license_req
                                        ?.CEUs || "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Special Requirements Sub-Accordion */}
                              <div className="sub-accordion-item">
                                <button
                                  className="sub-accordion-button"
                                  type="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#sub-special-req-${id}`}
                                  aria-expanded="true"
                                >
                                  <span>
                                    Special Requirements
                                    <InfoTooltip text="Additional state-mandated training required for licensure, such as implicit bias or abuse-prevention courses." />
                                  </span>
                                  <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 12 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M1 6.5L6 1.5L11 6.5"
                                      stroke="var(--mmp-text-secondary)"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                <div
                                  id={`sub-special-req-${id}`}
                                  className="collapse show sub-collapse"
                                >
                                  <div className="sub-accordion-body">
                                    <div className="paragraph-text">
                                      {selectedCityData?.nursing_license_req
                                        ?.Special_Requirements || "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Compact State Sub-Accordion */}
                              <div className="sub-accordion-item">
                                <button
                                  className="sub-accordion-button"
                                  type="button"
                                  data-bs-toggle="collapse"
                                  data-bs-target={`#sub-compact-state-${id}`}
                                  aria-expanded="true"
                                >
                                  <span>
                                    Compact State
                                    <InfoTooltip text="Whether this state participates in the Nurse Licensure Compact, letting multistate license holders practice here without a separate license." />
                                  </span>
                                  <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 12 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M1 6.5L6 1.5L11 6.5"
                                      stroke="var(--mmp-text-secondary)"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                <div
                                  id={`sub-compact-state-${id}`}
                                  className="collapse show sub-collapse"
                                >
                                  <div className="sub-accordion-body">
                                    {selectedCityData?.nursing_license_req?.Compact_State?.toLowerCase().includes(
                                      "no",
                                    ) ? (
                                      <div className="compact-state-alert">
                                        <span className="fw-bold text-danger">
                                          No
                                        </span>{" "}
                                        — CA does not participate in the Nurse
                                        Licensure Compact.
                                      </div>
                                    ) : (
                                      <div className="paragraph-text">
                                        {selectedCityData?.nursing_license_req
                                          ?.Compact_State || "N/A"}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Important Considerations Sub-Accordion */}
                              {selectedCityData?.nursing_license_req
                                ?.Important_Considerations_for_Compact_State_Nurses && (
                                <div className="sub-accordion-item">
                                  <button
                                    className="sub-accordion-button"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#sub-important-considerations-${id}`}
                                    aria-expanded="true"
                                  >
                                    <span>
                                      Important Considerations for Compact
                                      State Nurses
                                      <InfoTooltip text="Extra guidance for nurses relocating here on a multistate compact license." />
                                    </span>
                                    <svg
                                      width="10"
                                      height="6"
                                      viewBox="0 0 12 8"
                                      fill="none"
                                    >
                                      <path
                                        d="M1 6.5L6 1.5L11 6.5"
                                        stroke="var(--mmp-text-secondary)"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                  <div
                                    id={`sub-important-considerations-${id}`}
                                    className="collapse show sub-collapse"
                                  >
                                    <div className="sub-accordion-body">
                                      <div
                                        className="paragraph-text"
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            selectedCityData
                                              ?.nursing_license_req
                                              ?.Important_Considerations_for_Compact_State_Nurses ||
                                            "N/A",
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Demographics */}
                <div
                  className="demographics-content demographics-redesign-section"
                  data-tour="cd-demographics"
                >
                  <div className="custom-accordion p-0">
                    <div className="custom-accordion-item main-accordion">
                      <button
                        className="custom-accordion-button main-accordion-btn"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#demographics-main-${id}`}
                        aria-expanded="true"
                        aria-controls={`demographics-main-${id}`}
                      >
                        <span className="main-title">
                          {`${selectedCityData?.city_details?.city || "City"} Demographics`}
                          <InfoTooltip text="Population, income, age, race, and crime data for this city from public census and safety sources." />
                        </span>
                        <svg
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <div
                        id={`demographics-main-${id}`}
                        className="collapse show"
                      >
                        <div className="sub-accordions-wrapper">
                          {/* Education Levels */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-education-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Education Levels
                                <InfoTooltip text="Share of adult residents by highest level of education completed." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-education-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                {Object.entries(
                                  selectedCityData?.education || {},
                                ).map(([label, value], index) => (
                                  <div
                                    className="demo-progress-row"
                                    key={index}
                                  >
                                    <div className="demo-progress-header">
                                      <span className="demo-label">
                                        {label}
                                      </span>
                                      <span className="demo-value fw-bold">
                                        {value}%
                                      </span>
                                    </div>
                                    <div className="demo-progress-bar-container">
                                      <div
                                        className="demo-progress-fill bg-blue-light"
                                        style={{ width: `${value}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Racial Diversity */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-racial-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Racial Diversity
                                <InfoTooltip text="Racial and ethnic composition of this city's population." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-racial-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                {Object.entries(
                                  selectedCityData?.radical_diversity || {},
                                ).map(([label, value], index) => (
                                  <div
                                    className="demo-progress-row"
                                    key={index}
                                  >
                                    <div className="demo-progress-header">
                                      <span className="demo-label">
                                        {label}
                                      </span>
                                      <span className="demo-value fw-bold">
                                        {value}%
                                      </span>
                                    </div>
                                    <div className="demo-progress-bar-container">
                                      <div
                                        className="demo-progress-fill bg-blue-dark"
                                        style={{ width: `${value}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Income */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-income-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Income
                                <InfoTooltip text="Distribution of household income brackets in this city." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-income-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                {Object.entries(
                                  selectedCityData?.income || {},
                                ).map(([label, value], index) => (
                                  <div
                                    className="demo-progress-row"
                                    key={index}
                                  >
                                    <div className="demo-progress-header">
                                      <span className="demo-label">
                                        {label}
                                      </span>
                                      <span className="demo-value fw-bold">
                                        {value}%
                                      </span>
                                    </div>
                                    <div className="demo-progress-bar-container">
                                      <div
                                        className="demo-progress-fill bg-green"
                                        style={{ width: `${value}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Age */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-age-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Age
                                <InfoTooltip text="Age group breakdown of this city's population." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-age-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                {Object.entries(
                                  selectedCityData?.age || {},
                                ).map(([label, value], index) => (
                                  <div
                                    className="demo-progress-row"
                                    key={index}
                                  >
                                    <div className="demo-progress-header">
                                      <span className="demo-label">
                                        {label}
                                      </span>
                                      <span className="demo-value fw-bold">
                                        {value}%
                                      </span>
                                    </div>
                                    <div className="demo-progress-bar-container">
                                      <div
                                        className="demo-progress-fill bg-blue-light-alt"
                                        style={{ width: `${value}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Crime */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-crime-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Crime
                                <InfoTooltip text="Overall, violent, and property crime grades and score for this city." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-crime-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                <div className="info-row">
                                  <span className="label">
                                    Overall Crime Grade
                                  </span>
                                  <span className="value fw-bold text-warning">
                                    {grade || "N/A"}
                                  </span>
                                </div>
                                <div className="info-row">
                                  <span className="label">
                                    Violent Crime Grade
                                  </span>
                                  <span className="value fw-bold text-success">
                                    {violentGrade || "N/A"}
                                  </span>
                                </div>
                                <div className="info-row">
                                  <span className="label">
                                    Property Crime Grade
                                  </span>
                                  <span className="value fw-bold text-warning">
                                    {propertyGrade || "N/A"}
                                  </span>
                                </div>
                                <div className="info-row">
                                  <span className="label">Crime Score</span>
                                  <span className="value fw-bold">
                                    {selectedCityData?.city_details
                                      ?.crime_score || "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Median Household Income */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-income-household-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Median Household Income
                                <InfoTooltip text="Median household income for this city, from the U.S. Census Bureau's 5-year American Community Survey." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-income-household-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                <div className="income-highlight">
                                  <div className="income-amount">
                                    $
                                    {selectedCityData?.city_details
                                      ?.net_annual_salary
                                      ? Number(
                                          Number(
                                            selectedCityData.city_details
                                              .net_annual_salary,
                                          ).toFixed(0),
                                        ).toLocaleString("en-US")
                                      : "0"}
                                  </div>
                                  <div className="income-subtext">
                                    U.S. Census Bureau · 5-year ACS
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Other */}
                          <div className="sub-accordion-item">
                            <button
                              className="sub-accordion-button"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#sub-demo-other-${id}`}
                              aria-expanded="false"
                            >
                              <span>
                                Other
                                <InfoTooltip text="Additional city facts — total population, time zone or state capital status, and zip code." />
                              </span>
                              <svg
                                width="10"
                                height="6"
                                viewBox="0 0 12 8"
                                fill="none"
                              >
                                <path
                                  d="M1 6.5L6 1.5L11 6.5"
                                  stroke="var(--mmp-text-secondary)"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <div
                              id={`sub-demo-other-${id}`}
                              className="collapse show sub-collapse"
                            >
                              <div className="sub-accordion-body">
                                <div className="info-row">
                                  <span className="label">
                                    Total population
                                  </span>
                                  <span className="value fw-bold">
                                    {selectedCityData?.city_details
                                      ?.total_population
                                      ? Number(
                                          selectedCityData.city_details
                                            .total_population,
                                        ).toLocaleString("en-US")
                                      : "N/A"}
                                  </span>
                                </div>
                                {selectedCityData?.city_details?.timezone && (
                                  <div className="info-row">
                                    <span className="label">Time zone</span>
                                    <span className="value fw-bold">
                                      {selectedCityData.city_details.timezone}
                                    </span>
                                  </div>
                                )}
                                {selectedCityData?.city_details
                                  ?.state_capital && (
                                  <div className="info-row">
                                    <span className="label">State capital</span>
                                    <span className="value fw-bold">
                                      {
                                        selectedCityData.city_details
                                          .state_capital
                                      }
                                    </span>
                                  </div>
                                )}
                                {!selectedCityData?.city_details?.timezone && (
                                  <div className="info-row">
                                    <span className="label">Zip</span>
                                    <span className="value fw-bold">
                                      {selectedCityData?.city_details?.zip ||
                                        "N/A"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Nearby Schools */}
                <div
                  className="demographics-content nearby-schools-redesign-section"
                  data-tour="cd-schools"
                >
                  <div className="custom-accordion p-0">
                    <div className="custom-accordion-item main-accordion">
                      <button
                        className="custom-accordion-button main-accordion-btn"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#nearby-schools-${id}`}
                        aria-expanded="true"
                      >
                        <span className="main-title">
                          Nearby Schools
                          <InfoTooltip text="Schools near this city, filterable by level (elementary, middle, high)." />
                        </span>
                        <svg
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <div
                        id={`nearby-schools-${id}`}
                        className="collapse show"
                      >
                        <div className="accordion-body-content ">
                          <div className="d-flex justify-content-end mb-2">
                            <Select
                              onChange={(option) => setSelectedLevel(option)}
                              aria-label="Select school level"
                              defaultValue={{
                                value: "Elementary",
                                label: "Elementary",
                              }}
                              menuPosition="fixed"
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  borderRadius: "8px",
                                  borderColor: "var(--mmp-border)",
                                  minHeight: "36px",
                                  boxShadow: "none",
                                  backgroundColor: "var(--mmp-surface)",
                                  "&:hover": { borderColor: "var(--mmp-border)" },
                                }),
                                singleValue: (base) => ({
                                  ...base,
                                  color: "var(--mmp-text-primary)",
                                }),
                                menu: (base) => ({
                                  ...base,
                                  borderRadius: "8px",
                                  marginTop: "4px",
                                  backgroundColor: "var(--mmp-surface)",
                                  zIndex: "1000",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: state.isFocused
                                    ? "var(--mmp-accent-bg)"
                                    : "var(--mmp-surface)",
                                  color: "var(--mmp-text-primary)",
                                  cursor: "pointer",
                                }),
                              }}
                              options={[
                                // { value: "All", label: "All" },
                                {
                                  value: "Elementary",
                                  label: "Elementary",
                                },
                                { value: "Middle", label: "Middle" },
                                { value: "High", label: "High" },
                              ]}
                              className="react-select-schools"
                              classNamePrefix="react-select"
                            />
                          </div>

                          <div className="schools-list">
                            {schoolsLoading ? (
                              <div className="text-center p-4">
                                Loading schools...
                              </div>
                            ) : schoolsResultsStore?.length > 0 ? (
                              schoolsResultsStore.map((school) => {
                                let scoreColorClass = "bg-green";
                                if (school.review_score < 5)
                                  scoreColorClass = "bg-red";
                                else if (school.review_score < 8)
                                  scoreColorClass = "bg-orange";

                                return (
                                  <div
                                    className="school-card"
                                    key={school.id}
                                    onClick={() => handleSchoolClick(school)}
                                  >
                                    <div
                                      className={`school-score ${scoreColorClass}`}
                                    >
                                      {school.review_score}/10
                                    </div>
                                    <div className="school-details">
                                      <div className="school-name">
                                        {school.name}
                                      </div>
                                      {school.rating_band > 0 && (
                                        <div className="school-rating">
                                          <span className="star-icon">★</span>
                                          <span className="rating-val">
                                            {school.rating_band}
                                          </span>
                                          <span className="reviews-cnt">
                                            ({school.review_count} reviews)
                                          </span>
                                        </div>
                                      )}
                                      <div className="school-meta">
                                        {school.type}, {school.level} ·{" "}
                                        {school.student_count} students
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="no-data text-center p-3 text-muted">
                                No schools found for{" "}
                                {selectedLevel?.label || "the selected"} level.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {showModal && (
                    <Modal
                      show={showModal}
                      centered
                      className="otp-modal custom-modal"
                      size="md"
                    >
                      <Modal.Header>
                        <h5 className="modal-title">Error</h5>
                      </Modal.Header>
                      <Modal.Body className="pt-0">
                        <p
                          className="text-center pb-3 m-0"
                          style={{ fontSize: "1rem", fontWeight: "500" }}
                        >
                          This school does not have a website. <br /> Please try
                          again later.
                        </p>
                        <div className="d-flex justify-content-center pt-4">
                          <Button
                            className="btn btn-primary w-30 text-center"
                            onClick={() => setShowModal(false)}
                          >
                            Close
                          </Button>
                        </div>
                      </Modal.Body>
                    </Modal>
                  )}
                </div>

                {/* Annual Temperature */}
                <div
                  className="demographics-content annual-temp-redesign-section"
                  data-tour="cd-temp"
                >
                  <div className="custom-accordion p-0">
                    <div className="custom-accordion-item main-accordion">
                      <button
                        className="custom-accordion-button main-accordion-btn"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#annual-temp-${id}`}
                        aria-expanded="true"
                      >
                        <span className="main-title">
                          Annual Temperature
                          <InfoTooltip text="Average monthly high and low temperatures for this city throughout the year." />
                        </span>
                        <svg
                          width="12"
                          height="8"
                          viewBox="0 0 12 8"
                          fill="none"
                        >
                          <path
                            d="M1 6.5L6 1.5L11 6.5"
                            stroke="var(--mmp-text-secondary)"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <div id={`annual-temp-${id}`} className="collapse show">
                        <div className="accordion-body-content p-2">
                          <div className="annual-temp-chart" data-tour="cd-temp-graph">
                            <Line data={lineData} options={lineOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data source disclaimer — full width beneath both columns */}
            <div className="zillow-data-disclaimer">
              <div className="zillow-data-disclaimer__title">
                Data Disclaimer
              </div>
              <p>
                Housing data sourced from{" "}
                <a
                  href="https://www.zillow.com/research/data/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Zillow Research
                </a>
                .
              </p>
              <p>
                Home values shown represent the typical home value for the
                region as reported by Zillow&apos;s Home Value Index and are not
                statistical medians. Rental data is sourced from Zillow&apos;s
                Observed Rent Index. Data is refreshed periodically and may not
                reflect real-time market conditions.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <p>No cities found</p>
      )}

      <RestrictionModal
        isVisible={Boolean(jobViewLimitInfo)}
        onClose={() => setJobViewLimitInfo(null)}
        eventSource="job_view_limit"
        iconType="briefcase"
        heading="You've reached your daily job-view limit"
        subText={`You've viewed your ${jobViewLimitInfo?.limit ??
          5} free jobs for today. Upgrade to Pro for unlimited job views.`}
        infoText={`You're on the ${(jobViewLimitInfo?.planSlug || "free")
          .charAt(0)
          .toUpperCase() +
          (jobViewLimitInfo?.planSlug || "free").slice(
            1,
          )} plan · ${jobViewLimitInfo?.used ??
          0} of ${jobViewLimitInfo?.limit ?? 5} job views used today`}
        thirdCtaMode="newsletter"
      />

      <RestrictionModal
        isVisible={showUtilitiesUpgradeModal}
        onClose={() => setShowUtilitiesUpgradeModal(false)}
        eventSource="search_detail_utilities"
        iconType="wallet"
        thirdCtaMode="newsletter"
        heading="Want to add expenses for this city?"
        subText="Upgrade to track your expenses and get personalized insights."
        infoText="Pro users can add expenses, track budgets, and analyze cost of living in any city."
      />

      <RestrictionModal
        isVisible={showSaveCityModal}
        onClose={() => setShowSaveCityModal(false)}
        eventSource="save_city"
        iconType="search-pin"
        thirdCtaMode="newsletter"
        heading="Want to save this city?"
        subText="Upgrade to Pro to save cities and track them in Your Cities."
        infoText="Free users can save 1 city per day. Pro users can save unlimited cities and get personalized insights for each one."
      />

      <Modal
        show={showUtilitiesEditModal}
        onHide={() => setShowUtilitiesEditModal(false)}
        centered
        className="utilities-edit-modal"
        dialogClassName="utilities-edit-dialog"
        contentClassName="utilities-edit-content"
        backdropClassName="utilities-edit-backdrop"
      >
        <Modal.Body className="utilities-edit-body">
          <button
            type="button"
            className="utilities-edit-close"
            aria-label="Close"
            onClick={() => setShowUtilitiesEditModal(false)}
          >
            <FiX size={16} />
          </button>

          <div className="utilities-edit-header">
            <div className="utilities-edit-icon-badge">
              <CalculatorIcon />
            </div>
            <div className="utilities-edit-heading">
              <h5 className="utilities-edit-title">Monthly expenses</h5>
              <p className="utilities-edit-subtitle">
                Electricity, water, gas &amp; internet.
              </p>
            </div>
          </div>

          <div className="utilities-edit-input-wrap">
            <span className="utilities-edit-prefix">$</span>
            <input
              id="utilities-edit-input"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className="utilities-edit-input"
              value={utilitiesDraft}
              autoFocus
              onChange={(e) => setUtilitiesDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveUtilitiesDraft();
              }}
            />
            <span className="utilities-edit-suffix">/month</span>
          </div>

          <div className="utilities-edit-presets">
            {[300, 500, 750, 1000].map((preset) => {
              const active = Number(utilitiesDraft) === preset;
              return (
                <button
                  type="button"
                  key={preset}
                  className={`utilities-preset-chip ${
                    active ? "is-active" : ""
                  }`}
                  onClick={() => setUtilitiesDraft(String(preset))}
                >
                  ${preset}
                </button>
              );
            })}
          </div>

          <div className="utilities-edit-actions">
            <button
              type="button"
              className="utilities-edit-btn utilities-edit-btn-secondary"
              onClick={() => setShowUtilitiesEditModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="utilities-edit-btn utilities-edit-btn-primary"
              onClick={saveUtilitiesDraft}
            >
              Save changes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {shareModal && (
        <div
          className="share-modal-backdrop"
          onClick={closeShareModal}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 99998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="share-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "var(--mmp-surface)",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              zIndex: 99999,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                borderBottom: "1px solid var(--mmp-border)",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "var(--mmp-text-primary)",
                  margin: 0,
                  lineHeight: "24px",
                }}
              >
                Share City
              </h3>
              <button
                onClick={closeShareModal}
                aria-label="Close"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  color: "var(--mmp-text-secondary)",
                  fontSize: "24px",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 0 }}>
              {[
                {
                  key: "twitter",
                  label: "Share on Twitter",
                  color: "#1da1f2",
                  Icon: FaXTwitter,
                },
                {
                  key: "facebook",
                  label: "Share on Facebook",
                  color: "#1877f2",
                  Icon: FaFacebookF,
                },
                {
                  key: "whatsapp",
                  label: "Share on WhatsApp",
                  color: "#25d366",
                  Icon: FaWhatsapp,
                },
                {
                  key: "linkedin",
                  label: "Share on LinkedIn",
                  color: "#0077b5",
                  Icon: FaLinkedinIn,
                },
              ].map(({ key, label, color, Icon }) => (
                <div
                  key={key}
                  onClick={() => shareCityToSocialMedia(key)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 24px",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--mmp-border)",
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: color,
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 500 }}>
                    {label}
                  </span>
                </div>
              ))}

              <div
                onClick={() => shareCityToSocialMedia("copy")}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  borderBottomLeftRadius: "12px",
                  borderBottomRightRadius: "12px",
                }}
              >
                <Copy
                  style={{ width: "20px", height: "20px", color: "var(--mmp-text-secondary)" }}
                />
                <span style={{ fontSize: "14px", fontWeight: 500 }}>
                  Copy Link
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <GuidedTour
        isOpen={isTourOpen}
        steps={searchDetailTourSteps}
        onDone={finishTour}
      />
    </>
  );
};

export default SearchDetailHeader;
